import React, { FormEvent, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowUturnLeftIcon,
  ChatBubbleLeftRightIcon,
  ChevronLeftIcon,
  EllipsisVerticalIcon,
  HeartIcon,
  MegaphoneIcon,
  PlusIcon,
  WrenchScrewdriverIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { communityService, createCommunityClientRequestId } from '../../services/communityService';
import type {
  CommunityThreadPageResult,
  CommunityThreadRead,
} from '../../services/communityService';
import { notificationService } from '../../services/notificationService';
import {
  COMMUNITY_CATEGORY_BY_SLUG,
  COMMUNITY_REPORT_REASONS,
  canCreateCommunityThreadInCategory,
  canReplyToCommunityThread,
  canViewCommunityCategory,
  canViewCommunityThread,
  formatCommunityDate,
  getAccessibleCommunityCategories,
  getCommunityRoleLabel,
  type CommunityCategory,
  type CommunityPost,
  type CommunityReport,
  type CommunityReportOccurrence,
  type CommunityReportReason,
  type CommunityThread,
} from '../../shared/community';
import { Alert, Badge, Button, Modal, Spinner } from '../../components/common/UIComponents';
import {
  isActiveCommunityAdmin,
  isApprovedCommunityServiceAdmin,
  isServiceAdmin,
  type AccessClaims,
  type AccessProfile,
} from '../../utils/accessControl';
import { getFriendlyFirebaseRuntimeError } from '../../utils/firebaseRuntimeError';
import {
  useCommunityProviderState,
  type CommunityProviderLoadState,
} from './useCommunityProviderState';
import type { ServiceProvider } from '../../types/firestore';

const pageShellClass = 'rs-community-page';
const innerClass = 'rs-community-shell';
const publicPageShellClass = 'w-full px-3 py-4 sm:px-6 lg:px-8';
const publicInnerClass = 'mx-auto flex w-full max-w-6xl flex-col gap-4 sm:gap-5';

const getServiceForumAccessCopy = (
  userProfile: AccessProfile | null | undefined,
  claims: AccessClaims | null | undefined,
  providerLoadState: CommunityProviderLoadState,
  provider: ServiceProvider | null
): string | null => {
  if (!isServiceAdmin(userProfile, claims)) return null;
  if (userProfile?.status !== 'active') {
    return userProfile?.status === 'suspended'
      ? 'Konto serwisu jest zawieszone. Prywatne forum pozostaje niedostępne.'
      : userProfile?.status === 'rejected'
        ? 'Konto serwisu zostało odrzucone. Prywatne forum pozostaje niedostępne.'
        : 'Konto serwisu nie jest jeszcze aktywne. Prywatne forum pozostaje niedostępne.';
  }
  if (providerLoadState === 'loading') {
    return 'Sprawdzamy bieżący status profilu serwisu…';
  }
  if (providerLoadState === 'error') {
    return 'Nie udało się potwierdzić bieżącego statusu profilu serwisu. Odśwież stronę, aby spróbować ponownie.';
  }
  if (!claims?.isServiceAdmin && !claims?.isServiceProvider) {
    return 'Uprawnienie serwisu nie jest aktywne. Prywatne forum pozostaje niedostępne.';
  }
  if (!provider) {
    return 'Nie znaleziono aktywnego profilu serwisu. Prywatne forum pozostaje niedostępne.';
  }
  if (provider.isTestData === true) {
    return 'Profil testowy nie daje dostępu do prywatnego forum serwisantów.';
  }
  if (provider.verificationStatus === 'changes_requested') {
    return 'Profil serwisu wymaga uzupełnienia zmian przed dostępem do prywatnego forum.';
  }
  if (provider.verificationStatus !== 'approved' || provider.isVerified !== true) {
    return 'Profil serwisu czeka na pełne zatwierdzenie. Prywatne forum pozostaje niedostępne.';
  }
  return null;
};

const isOwn = (currentUid: string | undefined | null, authorId?: string | null): boolean =>
  Boolean(currentUid && authorId && currentUid === authorId);

type CommunityQuoteTarget = Pick<CommunityPost, 'id' | 'authorDisplayNameSnapshot' | 'body'>;

const normalizeCommunityText = (value?: string | null): string => String(value || '').trim();

const isLongCommunityText = (value: string, compactThreshold = 220): boolean =>
  value.length > compactThreshold || value.split('\n').length > 3;

const textClampClass = (lines: 2 | 3 | 4): string => {
  if (lines === 2) return 'line-clamp-2';
  if (lines === 3) return 'line-clamp-3';
  return 'line-clamp-4';
};

const timestampMillis = (value: unknown): number => {
  if (value && typeof (value as { toMillis?: unknown }).toMillis === 'function') {
    return (value as { toMillis: () => number }).toMillis();
  }
  return 0;
};

const timestampDate = (value: unknown): Date | null => {
  if (value && typeof (value as { toDate?: unknown }).toDate === 'function') {
    const date = (value as { toDate: () => Date }).toDate();
    return date instanceof Date ? date : null;
  }
  return null;
};

const formatCommunityActivityTime = (value?: unknown): string => {
  const date = timestampDate(value);
  if (!date) return 'brak daty';
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  if (isToday) {
    return date.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
  }
  return date.toLocaleDateString('pl-PL', {
    day: '2-digit',
    month: 'short',
    year: now.getFullYear() === date.getFullYear() ? undefined : 'numeric',
  });
};

const isThreadUnread = (
  thread: CommunityThread,
  read?: CommunityThreadRead,
  currentUid?: string | null
): boolean => {
  if (currentUid && thread.lastPostAuthorId === currentUid) return false;
  return timestampMillis(thread.lastActivityAt) > timestampMillis(read?.lastReadAt);
};

const getVisiblePostsCount = (thread: CommunityThread): number =>
  typeof thread.postsCount === 'number' && thread.postsCount > 0
    ? thread.postsCount
    : Math.max(1, (thread.repliesCount || 0) + 1);

const getAuthorInitials = (name?: string | null): string => {
  const parts = String(name || 'Użytkownik')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2);
  return (
    parts
      .map((part) => part[0])
      .join('')
      .toUpperCase() || 'U'
  );
};

const getPermissionStripCopy = (
  category: CommunityCategory,
  canCreate: boolean,
  canView: boolean
): string => {
  if (!canView) return 'Czytanie: brak dostępu · Pisanie: niedostępne';
  if (canCreate) return 'Czytasz · Możesz pisać';
  if (category.type === 'service_forum') return 'Czytasz · Pisanie: zatwierdzone serwisy';
  if (category.type === 'announcements') return 'Czytasz · Pisanie: administracja';
  if (category.type === 'gratitude') return 'Czytasz · Pisanie: klienci';
  return 'Czytasz · Pisanie zależy od roli';
};

const getOpeningPostLabel = (categoryType?: CommunityCategory['type']): string => {
  if (categoryType === 'ask_community') return 'Pytanie';
  if (categoryType === 'client_voice') return 'Głos klienta';
  if (categoryType === 'gratitude') return 'Podziękowanie';
  if (categoryType === 'announcements') return 'Ogłoszenie';
  return 'Temat';
};

const ExpandableText: React.FC<{
  text: string;
  expanded: boolean;
  onToggle: () => void;
  collapsedLines?: 2 | 3 | 4;
  forceToggle?: boolean;
  className?: string;
}> = ({ text, expanded, onToggle, collapsedLines = 4, forceToggle = false, className = '' }) => {
  const normalizedText = normalizeCommunityText(text);
  const canToggle = forceToggle || isLongCommunityText(normalizedText);

  if (!normalizedText) return null;

  return (
    <div className="space-y-1.5">
      <p
        className={`whitespace-pre-wrap break-words text-sm leading-relaxed text-primary-light-200 ${
          canToggle && !expanded ? textClampClass(collapsedLines) : ''
        } ${className}`}
      >
        {normalizedText}
      </p>
      {canToggle && (
        <button
          type="button"
          onClick={onToggle}
          className="rs-community-utility !min-w-0 px-1 text-xs font-semibold text-ratuj-200 hover:text-ratuj-100"
        >
          {expanded ? 'Pokaż mniej' : 'Pokaż więcej'}
        </button>
      )}
    </div>
  );
};

type CommunityErrorContext = 'load' | 'thread' | 'posts' | 'access' | 'create' | 'reply' | 'action';

const isFirebaseIndexError = (message: string, code: string): boolean =>
  code.includes('failed-precondition') &&
  (message.toLowerCase().includes('requires an index') ||
    message.toLowerCase().includes('create it here'));

const getErrorMessage = (error: unknown, context: CommunityErrorContext = 'action'): string => {
  const code = String((error as { code?: unknown })?.code || '');
  const message = String((error as { message?: unknown })?.message || '');
  if (isFirebaseIndexError(message, code)) {
    return context === 'create'
      ? 'Nie udało się zapisać wątku. Sprawdź uprawnienia albo spróbuj ponownie.'
      : context === 'posts'
        ? 'Nie udało się pobrać wpisów. Spróbuj ponownie.'
        : 'Nie udało się pobrać wątków. Spróbuj ponownie.';
  }
  if (code.includes('permission-denied')) {
    if (context === 'thread') return 'Nie masz dostępu do tego wątku.';
    if (context === 'posts') return 'Nie udało się pobrać wpisów. Spróbuj ponownie.';
    if (context === 'load' || context === 'access') return 'Nie masz dostępu do tego działu.';
    if (context === 'create')
      return 'Nie udało się zapisać wątku. Sprawdź uprawnienia albo spróbuj ponownie.';
    if (context === 'reply') return message || 'Nie możesz odpowiedzieć w tym wątku.';
    return 'Brak uprawnień do tej akcji.';
  }
  if (code.includes('not-found')) return 'Nie znaleziono wątku.';
  if (code.includes('resource-exhausted')) return message || 'Limit akcji został wykorzystany.';
  if (code.includes('failed-precondition'))
    return context === 'thread'
      ? 'Nie udało się pobrać wątku.'
      : context === 'posts'
        ? 'Nie udało się pobrać wpisów. Spróbuj ponownie.'
        : context === 'load'
          ? 'Nie udało się pobrać wątków. Spróbuj ponownie.'
          : message || 'Warunek wykonania nie jest spełniony.';
  if (context === 'thread') return 'Nie udało się pobrać wątku.';
  if (context === 'posts') return 'Nie udało się pobrać wpisów. Spróbuj ponownie.';
  if (code.includes('already-exists')) return message || 'Ten rekord już istnieje.';
  if (context === 'load') return 'Nie udało się pobrać wątków. Spróbuj ponownie.';
  if (context === 'create')
    return 'Nie udało się zapisać wątku. Sprawdź uprawnienia albo spróbuj ponownie.';
  return 'Nie udało się wykonać akcji.';
};

const CommunityStatusBadge: React.FC<{ thread: CommunityThread }> = ({ thread }) => {
  const chips: React.ReactNode[] = [];
  if (thread.status === 'hidden') chips.push(<Badge variant="error">Ukryty</Badge>);
  if (thread.status === 'closed') chips.push(<Badge variant="warning">Zamknięty</Badge>);
  if (thread.isPinned) chips.push(<Badge variant="accent">Przypięty</Badge>);
  if (!chips.length) return null;
  return (
    <>
      {chips.map((chip, index) => (
        <React.Fragment key={index}>{chip}</React.Fragment>
      ))}
    </>
  );
};

const CommunityStatusChips: React.FC<{ thread: CommunityThread }> = ({ thread }) => (
  <div className="flex flex-wrap items-center gap-1.5">
    <CommunityStatusBadge thread={thread} />
  </div>
);

const CommunitySkeletonRows: React.FC<{ rows?: number }> = ({ rows = 4 }) => (
  <div className="rs-community-list-surface" aria-label="Ładowanie listy">
    {Array.from({ length: rows }).map((_, index) => (
      <div
        key={index}
        className="rs-community-row grid grid-cols-[minmax(0,1fr)_4rem] gap-3 px-3 py-3 sm:px-4"
      >
        <div className="min-w-0 space-y-2">
          <div className="h-3.5 w-2/3 rounded bg-primary-dark-100/80" />
          <div className="h-2.5 w-full max-w-xl rounded bg-primary-dark-100/55" />
        </div>
        <div className="h-3 rounded bg-primary-dark-100/60" />
      </div>
    ))}
  </div>
);

const AuthorRoleChip: React.FC<{
  role?: string;
  categoryType?: CommunityCategory['type'];
}> = ({ role, categoryType }) => {
  if (role === 'admin') return <Badge variant="accent">Admin</Badge>;
  if (role === 'serviceAdmin') {
    if (categoryType === 'service_forum') return null;
    return <Badge variant="primary">Serwis</Badge>;
  }
  if (categoryType === 'gratitude') return <Badge variant="neutral">Klient</Badge>;
  if (categoryType === 'ask_community' || categoryType === 'client_voice') {
    return <Badge variant="neutral">Klient</Badge>;
  }
  return null;
};

const MenuItemButton: React.FC<{
  children: React.ReactNode;
  onClick: () => void;
  destructive?: boolean;
  disabled?: boolean;
}> = ({ children, onClick, destructive = false, disabled = false }) => (
  <button
    type="button"
    role="menuitem"
    onClick={onClick}
    disabled={disabled}
    className={`rs-community-menu-item block !min-h-11 w-full rounded-md px-3 py-2 text-left text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
      destructive
        ? 'text-status-error-400 hover:bg-status-error-500/10'
        : 'text-primary-light-200 hover:bg-primary-dark-100/75'
    }`}
  >
    {children}
  </button>
);

const ViewportDropdown: React.FC<{
  open: boolean;
  anchorRef: React.RefObject<HTMLElement>;
  width?: number;
  children: React.ReactNode;
  onClose: () => void;
}> = ({ open, anchorRef, width = 176, children, onClose }) => {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState({ top: 0, left: 16 });
  const closeAndReturnFocus = useCallback(() => {
    onClose();
    window.setTimeout(() => anchorRef.current?.focus(), 0);
  }, [anchorRef, onClose]);

  useEffect(() => {
    if (!open) return;

    const updatePosition = () => {
      const anchor = anchorRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const viewportWidth = window.visualViewport?.width ?? window.innerWidth;
      const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
      const collisionPadding = 16;
      const menuWidth = Math.min(width, viewportWidth - collisionPadding * 2);
      const menuHeight = menuRef.current?.offsetHeight ?? 148;
      const preferredLeft = rect.right - menuWidth;
      const left = Math.min(
        Math.max(preferredLeft, collisionPadding),
        viewportWidth - menuWidth - collisionPadding
      );
      const preferredTop = rect.bottom + 8;
      const top =
        preferredTop + menuHeight + collisionPadding > viewportHeight
          ? Math.max(collisionPadding, rect.top - menuHeight - 8)
          : preferredTop;

      setPosition({ top, left });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    window.visualViewport?.addEventListener('resize', updatePosition);
    window.visualViewport?.addEventListener('scroll', updatePosition);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
      window.visualViewport?.removeEventListener('resize', updatePosition);
      window.visualViewport?.removeEventListener('scroll', updatePosition);
    };
  }, [anchorRef, open, width]);

  useEffect(() => {
    if (!open) return;

    const focusTimeoutId = window.setTimeout(() => {
      menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]:not(:disabled)')?.focus();
    }, 0);

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      if (anchorRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      onClose();
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeAndReturnFocus();
        return;
      }
      if (event.key === 'Tab') {
        onClose();
        return;
      }
      if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
      const items = Array.from(
        menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not(:disabled)') || []
      );
      if (!items.length) return;
      event.preventDefault();
      const currentIndex = items.indexOf(document.activeElement as HTMLElement);
      const nextIndex =
        event.key === 'Home'
          ? 0
          : event.key === 'End'
            ? items.length - 1
            : event.key === 'ArrowUp'
              ? (currentIndex - 1 + items.length) % items.length
              : (currentIndex + 1) % items.length;
      items[nextIndex]?.focus();
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      window.clearTimeout(focusTimeoutId);
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [anchorRef, closeAndReturnFocus, onClose, open]);

  if (!open) return null;

  return (
    <div
      ref={menuRef}
      role="menu"
      aria-orientation="vertical"
      className="rs-surface-overlay fixed z-[80] p-1 shadow-card"
      style={{
        top: position.top,
        left: position.left,
        width,
        maxWidth: 'calc(100vw - 32px)',
      }}
    >
      {children}
    </div>
  );
};

const PostActionMenu: React.FC<{
  post: CommunityPost;
  canEdit: boolean;
  admin: boolean;
  busyAction: string;
  onEdit: (returnFocusElement: HTMLElement | null) => void;
  onReport: (returnFocusElement: HTMLElement | null) => void;
  onModerate: (
    action: 'hide_post' | 'restore_post',
    postId: string,
    returnFocusElement: HTMLElement | null
  ) => void;
}> = ({ post, canEdit, admin, busyAction, onEdit, onReport, onModerate }) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const moderationAction = post.status === 'hidden' ? 'restore_post' : 'hide_post';
  const moderationPending = busyAction === `${moderationAction}-${post.id}`;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-label="Więcej akcji wpisu"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((value) => !value)}
        className="rs-community-utility"
      >
        <EllipsisVerticalIcon className="h-5 w-5" />
      </button>
      <ViewportDropdown open={open} anchorRef={triggerRef} onClose={() => setOpen(false)}>
        {canEdit && (
          <MenuItemButton
            onClick={() => {
              setOpen(false);
              onEdit(triggerRef.current);
            }}
          >
            Edytuj
          </MenuItemButton>
        )}
        <MenuItemButton
          onClick={() => {
            setOpen(false);
            onReport(triggerRef.current);
          }}
        >
          Zgłoś
        </MenuItemButton>
        {admin && (
          <MenuItemButton
            destructive={post.status !== 'hidden'}
            disabled={moderationPending}
            onClick={() => {
              setOpen(false);
              onModerate(moderationAction, post.id, triggerRef.current);
            }}
          >
            {post.status === 'hidden' ? 'Przywróć' : 'Ukryj'}
          </MenuItemButton>
        )}
      </ViewportDropdown>
    </>
  );
};

const ThreadRow: React.FC<{ thread: CommunityThread; compact?: boolean; unread?: boolean }> = ({
  thread,
  compact = false,
  unread = false,
}) => (
  <Link
    to={`/app/community/thread/${thread.id}`}
    className={`rs-community-row rs-community-thread-row group ${
      unread ? 'rs-community-row--unread' : ''
    }`}
  >
    <div className="grid gap-2 md:grid-cols-[minmax(0,1fr)_5rem_9rem] md:items-center">
      <div className="min-w-0">
        <div className="flex min-w-0 items-start gap-2">
          <span className="sr-only">{unread ? 'Nieprzeczytany wątek. ' : ''}</span>
          <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary-dark-100/30">
            {thread.isPinned ? (
              <MegaphoneIcon className="h-3.5 w-3.5 text-gold-300" />
            ) : (
              <ChatBubbleLeftRightIcon className="h-3.5 w-3.5 text-ratuj-300" />
            )}
          </span>
          <div className="min-w-0">
            <h2 className="line-clamp-2 break-words text-sm font-semibold leading-snug text-primary-light-100 group-hover:text-ratuj-200 sm:text-base">
              {thread.title}
            </h2>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              {unread && (
                <span className="text-[11px] font-semibold text-ratuj-200">Nowy wpis</span>
              )}
              <CommunityStatusBadge thread={thread} />
            </div>
          </div>
        </div>
        {!compact && thread.bodyExcerpt && (
          <p className="mt-1 hidden line-clamp-1 break-words pl-11 text-xs leading-relaxed text-primary-light-300 sm:block">
            {thread.bodyExcerpt}
          </p>
        )}
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 pl-9 text-xs text-primary-light-400">
          <span>{thread.authorDisplayNameSnapshot || 'Użytkownik'}</span>
          <span>{thread.repliesCount || 0} odp.</span>
          {thread.targetServiceNameSnapshot && <span>dla {thread.targetServiceNameSnapshot}</span>}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 pl-9 text-xs text-primary-light-400 md:contents md:pl-0 md:text-right">
        <span className="md:block">
          <strong className="font-semibold text-primary-light-100">
            {getVisiblePostsCount(thread)}
          </strong>{' '}
          <span className="md:hidden">wpisy</span>
        </span>
        <span className="truncate md:block">
          {thread.lastPostAuthorDisplayNameSnapshot ||
            thread.authorDisplayNameSnapshot ||
            'Użytkownik'}{' '}
          · {formatCommunityActivityTime(thread.lastActivityAt)}
        </span>
      </div>
    </div>
  </Link>
);

const PublicCommunityTeaserRow: React.FC<{ thread: CommunityThread }> = ({ thread }) => {
  const title = normalizeCommunityText(thread.title) || 'Temat bez tytułu';
  const excerpt = normalizeCommunityText(thread.bodyExcerpt);
  const author = normalizeCommunityText(thread.authorDisplayNameSnapshot) || 'Autor niepodany';
  const targetService = normalizeCommunityText(thread.targetServiceNameSnapshot);
  const isAnnouncement = thread.categoryType === 'announcements';

  return (
    <Link
      to={`/app/community/thread/${thread.id}`}
      className="rs-community-row rs-public-community-row group focus-outline"
      aria-label={`${isAnnouncement ? 'Ogłoszenie' : 'Podziękowanie'}: ${title}`}
    >
      <span className="rs-public-community-row-icon" aria-hidden="true">
        {isAnnouncement ? <MegaphoneIcon /> : <HeartIcon />}
      </span>
      <span className="min-w-0">
        <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-primary-light-400">
            {isAnnouncement ? 'Ogłoszenie' : 'Podziękowanie'}
          </span>
          {thread.status === 'closed' && (
            <span className="text-[11px] font-semibold text-gold-200">Zamknięte</span>
          )}
        </span>
        <span className="mt-1 block line-clamp-2 break-words text-sm font-semibold leading-snug text-primary-light-100 group-hover:text-ratuj-200 sm:text-base">
          {title}
        </span>
        {excerpt && (
          <span className="mt-1 block line-clamp-2 break-words text-xs leading-relaxed text-primary-light-300">
            {excerpt}
          </span>
        )}
        <span className="mt-1.5 flex min-w-0 flex-wrap gap-x-2 gap-y-1 text-xs text-primary-light-400">
          <span className="break-words">{author}</span>
          {targetService && <span className="break-words">dla {targetService}</span>}
          <span>{formatCommunityActivityTime(thread.lastActivityAt)}</span>
        </span>
      </span>
    </Link>
  );
};

const PublicCommunitySkeletonSection: React.FC<{ label: string; rows: number }> = ({
  label,
  rows,
}) => (
  <section className="min-w-0 space-y-2" aria-busy="true" aria-label={`Ładowanie: ${label}`}>
    <div className="h-5 w-36 animate-pulse rounded bg-primary-dark-100/70" />
    <div className="rs-community-list-surface">
      {Array.from({ length: rows }).map((_, index) => (
        <div key={index} className="rs-public-community-row animate-pulse" aria-hidden="true">
          <span className="rs-public-community-row-icon bg-primary-dark-100/70" />
          <span className="min-w-0 space-y-2">
            <span className="block h-2.5 w-24 rounded bg-primary-dark-100/55" />
            <span className="block h-3.5 w-4/5 rounded bg-primary-dark-100/80" />
            <span className="block h-2.5 w-full rounded bg-primary-dark-100/55" />
            <span className="block h-2.5 w-2/5 rounded bg-primary-dark-100/55" />
          </span>
        </div>
      ))}
    </div>
  </section>
);

const PublicCommunitySection: React.FC<{
  id: string;
  title: string;
  description: string;
  emptyCopy: string;
  threads: CommunityThread[];
}> = ({ id, title, description, emptyCopy, threads }) => (
  <section className="min-w-0 space-y-2" aria-labelledby={id}>
    <div className="min-w-0 px-1">
      <h2 id={id} className="break-words text-base font-semibold text-primary-light-100 sm:text-lg">
        {title}
      </h2>
      <p className="mt-0.5 text-xs leading-relaxed text-primary-light-400">{description}</p>
    </div>
    <div className="rs-community-list-surface">
      {threads.length === 0 ? (
        <p className="rs-community-state rounded-none text-sm" role="status">
          {emptyCopy}
        </p>
      ) : (
        threads.map((thread) => <PublicCommunityTeaserRow key={thread.id} thread={thread} />)
      )}
    </div>
  </section>
);

const CommunityHeader: React.FC<{
  label?: string;
  title: string;
  description?: string;
  actions?: React.ReactNode;
  actionsPlacement?: 'side' | 'top';
  status?: React.ReactNode;
  backLink?: { to: string; label: string };
  protectedStyle?: boolean;
  clampDescription?: boolean;
}> = ({
  label = 'Społeczność',
  title,
  description,
  actions,
  actionsPlacement = 'side',
  status,
  backLink,
  protectedStyle = false,
  clampDescription = true,
}) => (
  <div
    className={
      protectedStyle
        ? 'rs-community-identity'
        : 'rounded-lg border border-primary-dark-300/38 bg-primary-dark-200/38 px-3 py-2.5 sm:px-4 sm:py-3'
    }
  >
    {backLink && (
      <Link
        to={backLink.to}
        className="rs-community-utility -ml-1 mb-1 w-fit !min-w-0 px-1 text-sm text-primary-light-300 hover:text-ratuj-200"
      >
        <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" />
        {backLink.label}
      </Link>
    )}
    {actionsPlacement === 'top' ? (
      <div className="min-w-0">
        <div className="flex min-w-0 items-start justify-between gap-2">
          <p className="min-w-0 break-words text-[11px] font-semibold uppercase tracking-[0.14em] text-ratuj-300">
            {label}
          </p>
          {actions && <div className="flex shrink-0 items-start gap-1.5">{actions}</div>}
        </div>
        <h1 className="mt-1 break-words text-xl font-semibold leading-tight text-primary-light-100 sm:text-2xl">
          {title}
        </h1>
        {status}
        {description && (
          <p
            className={`mt-1 max-w-3xl text-sm leading-relaxed text-primary-light-300 ${
              clampDescription ? 'line-clamp-2' : ''
            }`}
          >
            {description}
          </p>
        )}
      </div>
    ) : (
      <div className="flex flex-col gap-2.5 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0">
          <div className="flex min-w-0 items-start justify-between gap-2">
            <p className="min-w-0 break-words text-[11px] font-semibold uppercase tracking-[0.14em] text-ratuj-300">
              {label}
            </p>
          </div>
          <h1 className="mt-1 break-words text-xl font-semibold leading-tight text-primary-light-100 sm:text-2xl">
            {title}
          </h1>
          {status}
          {description && (
            <p
              className={`mt-1 max-w-3xl text-sm leading-relaxed text-primary-light-300 ${
                clampDescription ? 'line-clamp-2' : ''
              }`}
            >
              {description}
            </p>
          )}
        </div>
        {actions && (
          <div className="flex shrink-0 flex-wrap items-start justify-start gap-2 md:justify-end">
            {actions}
          </div>
        )}
      </div>
    )}
  </div>
);

const ThreadForm: React.FC<{
  category: CommunityCategory;
  canCreate: boolean;
  onCreated: (threadId: string) => void | Promise<void>;
  onSubmittingChange?: (submitting: boolean) => void;
  initialFocusRef?: React.MutableRefObject<HTMLInputElement | null>;
}> = ({ category, canCreate, onCreated, onSubmittingChange, initialFocusRef }) => {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [targetServiceProviderId, setTargetServiceProviderId] = useState('');
  const [serviceOptions, setServiceOptions] = useState<Array<{ id: string; serviceName: string }>>(
    []
  );
  const [loadingServices, setLoadingServices] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ title?: string; body?: string }>({});
  const submitInFlightRef = useRef(false);
  const threadClientRequestIdRef = useRef<string | null>(null);
  const threadRequestPayloadRef = useRef<string | null>(null);
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);
  const titleId = useId();
  const bodyId = useId();
  const serviceId = useId();
  const errorId = useId();

  useEffect(() => {
    if (category.type !== 'gratitude') return;
    setLoadingServices(true);
    communityService
      .getApprovedServiceProviderOptions()
      .then(setServiceOptions)
      .catch((error) => {
        console.error('Nie udało się pobrać serwisów do podziękowań:', error);
      })
      .finally(() => setLoadingServices(false));
  }, [category.type]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (submitInFlightRef.current) {
      return;
    }

    if (!canCreate) {
      setError('Nie masz uprawnień do utworzenia wątku w tym dziale.');
      return;
    }
    const nextFieldErrors: { title?: string; body?: string } = {};
    if (title.trim().length < 6) {
      nextFieldErrors.title = 'Tytuł musi mieć co najmniej 6 znaków.';
    }
    if (body.trim().length < 2) {
      nextFieldErrors.body = 'Treść musi mieć co najmniej 2 znaki.';
    }
    setFieldErrors(nextFieldErrors);
    if (nextFieldErrors.title) {
      initialFocusRef?.current?.focus();
      return;
    }
    if (nextFieldErrors.body) {
      bodyRef.current?.focus();
      return;
    }
    submitInFlightRef.current = true;
    setSubmitting(true);
    onSubmittingChange?.(true);
    setError(null);
    try {
      const normalizedTitle = title.trim();
      const normalizedBody = body.trim();
      const normalizedTargetServiceProviderId = targetServiceProviderId.trim();
      const requestPayload = JSON.stringify({
        categorySlug: category.slug,
        title: normalizedTitle,
        body: normalizedBody,
        targetServiceProviderId: normalizedTargetServiceProviderId,
      });
      if (!threadClientRequestIdRef.current || threadRequestPayloadRef.current !== requestPayload) {
        threadClientRequestIdRef.current = createCommunityClientRequestId();
        threadRequestPayloadRef.current = requestPayload;
      }
      const result = await communityService.createThread({
        categorySlug: category.slug,
        title: normalizedTitle,
        body: normalizedBody,
        clientRequestId: threadClientRequestIdRef.current,
        targetServiceProviderId: normalizedTargetServiceProviderId || undefined,
      });
      await onCreated(result.threadId);
      threadClientRequestIdRef.current = null;
      threadRequestPayloadRef.current = null;
    } catch (submitError) {
      setError(getErrorMessage(submitError, 'create'));
    } finally {
      submitInFlightRef.current = false;
      setSubmitting(false);
      onSubmittingChange?.(false);
    }
  };

  const helper =
    category.type === 'client_voice'
      ? 'Napisz spokojnie, co nie działa albo co warto poprawić. Nie podawaj danych prywatnych.'
      : category.type === 'ask_community'
        ? 'To miejsce ogólnych pytań. Pilną sprawę konkretnego zgłoszenia prowadź w czacie zgłoszenia.'
        : category.type === 'gratitude'
          ? 'Podziękowanie nie jest opinią ani oceną. Jeśli wskażesz serwis, może odpowiedzieć raz.'
          : category.type === 'announcements'
            ? 'Ogłoszenie będzie widoczne w społeczności i publicznym podglądzie.'
            : 'Załóż wątek dla zatwierdzonych serwisantów.';

  return (
    <form onSubmit={submit} noValidate className="space-y-4">
      <p className="text-sm text-primary-light-300">{helper}</p>
      {error && (
        <div role="alert">
          <Alert variant="error" className="scroll-mt-4">
            {error}
          </Alert>
        </div>
      )}
      <div className="space-y-1.5">
        <label htmlFor={titleId} className="block text-sm font-semibold text-primary-light-200">
          Tytuł wątku
        </label>
        <input
          id={titleId}
          ref={initialFocusRef}
          value={title}
          onChange={(event) => {
            setTitle(event.target.value);
            if (fieldErrors.title) setFieldErrors((current) => ({ ...current, title: undefined }));
          }}
          placeholder={category.type === 'gratitude' ? 'Za co chcesz podziękować?' : 'Tytuł wątku'}
          className="rs-community-input text-sm placeholder-primary-light-400"
          minLength={6}
          maxLength={140}
          aria-invalid={Boolean(fieldErrors.title)}
          aria-describedby={fieldErrors.title ? errorId : undefined}
          required
        />
        {fieldErrors.title && (
          <p id={errorId} className="rs-field-error">
            {fieldErrors.title}
          </p>
        )}
      </div>
      {category.type === 'gratitude' && (
        <div className="space-y-1.5">
          <label htmlFor={serviceId} className="block text-sm font-semibold text-primary-light-200">
            Serwis, któremu dziękujesz{' '}
            <span className="font-normal text-primary-light-400">(opcjonalnie)</span>
          </label>
          <select
            id={serviceId}
            value={targetServiceProviderId}
            onChange={(event) => setTargetServiceProviderId(event.target.value)}
            className="rs-community-input text-sm"
          >
            <option value="">
              {loadingServices ? 'Ładowanie serwisów...' : 'Bez wskazania konkretnego serwisu'}
            </option>
            {serviceOptions.map((option) => (
              <option key={option.id} value={option.id}>
                {option.serviceName}
              </option>
            ))}
          </select>
        </div>
      )}
      <div className="space-y-1.5">
        <div className="flex items-baseline justify-between gap-3">
          <label htmlFor={bodyId} className="block text-sm font-semibold text-primary-light-200">
            Treść
          </label>
          <span className="text-xs text-primary-light-400">{body.length} znaków</span>
        </div>
        <textarea
          id={bodyId}
          ref={bodyRef}
          value={body}
          onChange={(event) => {
            setBody(event.target.value);
            if (fieldErrors.body) setFieldErrors((current) => ({ ...current, body: undefined }));
          }}
          placeholder="Opisz temat rzeczowo i bez danych prywatnych"
          rows={6}
          className="rs-community-input min-h-[10rem] resize-y text-sm leading-relaxed placeholder-primary-light-400"
          minLength={2}
          maxLength={
            category.type === 'announcements' ? 8000 : category.type === 'gratitude' ? 2500 : 5000
          }
          aria-invalid={Boolean(fieldErrors.body)}
          aria-describedby={fieldErrors.body ? `${errorId}-body` : undefined}
          required
        />
        {fieldErrors.body && (
          <p id={`${errorId}-body`} className="rs-field-error">
            {fieldErrors.body}
          </p>
        )}
      </div>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <span className="text-xs text-primary-light-400">
          Opublikowanie zapisze wątek w tym dziale.
        </span>
        <Button type="submit" isLoading={submitting} disabled={!canCreate} className="!min-h-11">
          Opublikuj
        </Button>
      </div>
    </form>
  );
};

const ReportDialog: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  threadId: string;
  postId?: string;
  returnFocusRef?: React.RefObject<HTMLElement>;
}> = ({ isOpen, onClose, threadId, postId, returnFocusRef }) => {
  const [reason, setReason] = useState<CommunityReportReason>('spam');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const titleId = useId();
  const reasonId = useId();
  const messageId = useId();
  const reasonSelectRef = useRef<HTMLSelectElement | null>(null);
  const submitInFlightRef = useRef(false);
  const reportClientRequestIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      reportClientRequestIdRef.current = null;
    }
  }, [isOpen, postId, threadId]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (submitInFlightRef.current) {
      return;
    }
    submitInFlightRef.current = true;
    setBusy(true);
    setStatus(null);
    try {
      const clientRequestId = reportClientRequestIdRef.current || createCommunityClientRequestId();
      reportClientRequestIdRef.current = clientRequestId;
      await communityService.reportContent({
        targetType: postId ? 'post' : 'thread',
        threadId,
        postId,
        reason,
        message,
        clientRequestId,
      });
      setStatus('Dziękujemy. Zgłoszenie trafiło do moderacji.');
    } catch (error) {
      setStatus(getErrorMessage(error));
    } finally {
      submitInFlightRef.current = false;
      setBusy(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      ariaLabelledBy={titleId}
      initialFocusRef={reasonSelectRef}
      returnFocusRef={returnFocusRef}
    >
      <form onSubmit={submit} className="space-y-4 p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 id={titleId} className="text-base font-semibold text-primary-light-100">
              Zgłoś treść
            </h2>
            <p className="mt-1 text-sm text-primary-light-300">
              Zgłoszenie jest widoczne tylko dla administracji.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Zamknij zgłoszenie treści"
            className="rs-community-utility"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        {status && (
          <div
            role={status.startsWith('Dziękujemy') ? 'status' : 'alert'}
            aria-live={status.startsWith('Dziękujemy') ? 'polite' : 'assertive'}
          >
            <Alert variant={status.startsWith('Dziękujemy') ? 'success' : 'error'}>{status}</Alert>
          </div>
        )}
        <div className="space-y-1.5">
          <label htmlFor={reasonId} className="block text-sm font-semibold text-primary-light-200">
            Powód zgłoszenia
          </label>
          <select
            id={reasonId}
            ref={reasonSelectRef}
            value={reason}
            onChange={(event) => setReason(event.target.value as CommunityReportReason)}
            className="rs-community-input text-sm"
          >
            {COMMUNITY_REPORT_REASONS.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1.5">
          <label htmlFor={messageId} className="block text-sm font-semibold text-primary-light-200">
            Opis <span className="font-normal text-primary-light-400">(opcjonalnie)</span>
          </label>
          <textarea
            id={messageId}
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            rows={4}
            maxLength={1000}
            placeholder="Dodaj kontekst dla moderatora"
            className="rs-community-input resize-y text-sm placeholder-primary-light-400"
          />
        </div>
        <Button type="submit" isLoading={busy} className="!min-h-11">
          Wyślij zgłoszenie
        </Button>
      </form>
    </Modal>
  );
};

export const PublicCommunityPage: React.FC = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [announcements, setAnnouncements] = useState<CommunityThread[]>([]);
  const [gratitude, setGratitude] = useState<CommunityThread[]>([]);
  const [retryVersion, setRetryVersion] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    communityService
      .getPublicPreview()
      .then((result) => {
        if (cancelled) return;
        setAnnouncements(result.announcements);
        setGratitude(result.gratitude);
      })
      .catch((loadError) => {
        if (cancelled) return;
        console.error('Nie udało się załadować publicznej społeczności:', loadError);
        setError('Nie udało się pobrać publicznych ogłoszeń i podziękowań.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [retryVersion]);

  const isTotalEmpty = announcements.length === 0 && gratitude.length === 0;

  return (
    <div className={`${publicPageShellClass} rs-public-community-page`}>
      <div className={publicInnerClass}>
        <CommunityHeader
          protectedStyle
          clampDescription={false}
          label="Podgląd publiczny"
          title="Ogłoszenia i podziękowania społeczności"
          description="To spokojny, tylko do odczytu wycinek Community. Publicznie pokazujemy wyłącznie wybrane ogłoszenia i podziękowania; pytania, odpowiedzi i pełne rozmowy są dostępne w chronionej społeczności."
          actions={
            <div className="rs-public-community-actions">
              <Link
                to={currentUser ? '/app/community' : '/login'}
                className="rs-action-primary rs-action-md focus-outline"
              >
                {currentUser ? 'Wejdź do społeczności' : 'Zaloguj się do społeczności'}
              </Link>
              <nav className="rs-public-community-quiet-nav" aria-label="Pozostałe możliwości">
                <Link to="/map" className="rs-public-community-quiet-link focus-outline">
                  Znajdź serwis
                </Link>
                <Link
                  to="/register/service"
                  className="rs-public-community-quiet-link focus-outline"
                >
                  Zgłoś serwis
                </Link>
              </nav>
            </div>
          }
        />
        {loading ? (
          <div className="rs-public-community-grid" role="status">
            <span className="sr-only">Ładowanie publicznego podglądu społeczności.</span>
            <PublicCommunitySkeletonSection label="publiczne ogłoszenia" rows={3} />
            <PublicCommunitySkeletonSection label="podziękowania" rows={3} />
          </div>
        ) : error ? (
          <div className="rs-community-state" role="alert">
            <h2 className="text-base font-semibold text-primary-light-100">
              Podgląd jest chwilowo niedostępny
            </h2>
            <p className="mt-1 max-w-2xl text-sm leading-relaxed">{error}</p>
            <Button
              type="button"
              variant="secondary"
              className="mt-3 !min-h-11"
              onClick={() => setRetryVersion((version) => version + 1)}
            >
              Spróbuj ponownie
            </Button>
          </div>
        ) : isTotalEmpty ? (
          <div className="rs-community-state" role="status">
            <h2 className="text-base font-semibold text-primary-light-100">
              Brak publicznych wpisów
            </h2>
            <p className="mt-1 max-w-2xl text-sm leading-relaxed">
              Nie ma teraz ogłoszeń ani podziękowań udostępnionych w podglądzie. Pełna społeczność
              pozostaje dostępna po zalogowaniu.
            </p>
          </div>
        ) : (
          <div className="rs-public-community-grid">
            <PublicCommunitySection
              id="public-community-announcements"
              title="Publiczne ogłoszenia"
              description="Ważne informacje opublikowane przez administrację."
              emptyCopy="Nie ma teraz publicznych ogłoszeń."
              threads={announcements}
            />
            <PublicCommunitySection
              id="public-community-gratitude"
              title="Podziękowania"
              description="Wybrane podziękowania klientów — bez ocen, rankingu i języka recenzji."
              emptyCopy="Nie ma teraz publicznych podziękowań."
              threads={gratitude}
            />
          </div>
        )}
        <aside className="rs-community-access-strip" aria-label="Zasady publicznego podglądu">
          Community służy spokojnej wymianie wiedzy. Nie zastępuje zgłoszenia naprawy, kontaktu w
          konkretnej sprawie ani ścieżki reklamacyjnej.
        </aside>
      </div>
    </div>
  );
};

export const CommunityHomePage: React.FC = () => {
  const { currentUser, userProfile, claims } = useAuth();
  const { provider, loadState: providerLoadState } = useCommunityProviderState();
  const communityProvider = providerLoadState === 'ready' ? provider : null;
  const [latest, setLatest] = useState<CommunityThread[]>([]);
  const [reads, setReads] = useState<Record<string, CommunityThreadRead>>({});
  const [activityErrors, setActivityErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryVersion, setRetryVersion] = useState(0);
  const serviceAccessCopy = getServiceForumAccessCopy(
    userProfile,
    claims,
    providerLoadState,
    communityProvider
  );
  const categories = useMemo(() => communityService.getCategories(), []);
  const visibleCategorySlugs = useMemo(
    () =>
      getAccessibleCommunityCategories(categories, userProfile, claims, communityProvider).map(
        (category) => category.slug
      ),
    [categories, claims, communityProvider, userProfile]
  );
  const orderedCategories = useMemo(() => {
    const priorities = isApprovedCommunityServiceAdmin(userProfile, claims, communityProvider)
      ? ['forum-serwisantow', 'zapytaj-spolecznosc', 'glos-klienta', 'podziekowania', 'ogloszenia']
      : userProfile?.role === 'client'
        ? [
            'zapytaj-spolecznosc',
            'glos-klienta',
            'podziekowania',
            'ogloszenia',
            'forum-serwisantow',
          ]
        : categories.map((category) => category.slug);
    const priorityBySlug = new Map(priorities.map((slug, index) => [slug, index]));
    return [...categories].sort(
      (first, second) =>
        (priorityBySlug.get(first.slug) ?? first.order) -
        (priorityBySlug.get(second.slug) ?? second.order)
    );
  }, [categories, claims, communityProvider, userProfile]);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    setActivityErrors({});
    Promise.all([
      communityService.getLatestActivityByCategory(visibleCategorySlugs),
      currentUser?.uid ? communityService.getThreadReads(currentUser.uid) : Promise.resolve([]),
    ])
      .then(([activityResults, nextReads]) => {
        if (!active) return;
        const nextErrors = activityResults.reduce<Record<string, string>>((acc, result) => {
          if (result.error) {
            console.error(
              `Nie udało się pobrać aktywności społeczności dla działu ${result.categorySlug}:`,
              result.error
            );
            acc[result.categorySlug] = 'Nie udało się pobrać aktywności. Spróbuj odświeżyć stronę.';
          }
          return acc;
        }, {});
        setLatest(
          activityResults
            .flatMap((result) => result.threads)
            .sort(
              (first, second) =>
                timestampMillis(second.lastActivityAt) - timestampMillis(first.lastActivityAt)
            )
        );
        setActivityErrors(nextErrors);
        setReads(
          nextReads.reduce<Record<string, CommunityThreadRead>>((acc, read) => {
            acc[read.threadId] = read;
            return acc;
          }, {})
        );
      })
      .catch((loadError) => {
        if (!active) return;
        console.error('Nie udało się pobrać aktywności społeczności:', loadError);
        setError('Nie udało się pobrać danych społeczności. Spróbuj odświeżyć stronę.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [currentUser?.uid, retryVersion, visibleCategorySlugs]);

  return (
    <div className={pageShellClass}>
      <div className={innerClass}>
        <CommunityHeader
          label="Forum naprawcze"
          title="Społeczność"
          description="Wybierz dział i dołącz do rzeczowej rozmowy o naprawie."
          protectedStyle
        />
        {serviceAccessCopy && (
          <div className="rs-community-access-strip rs-community-access-strip--restricted">
            {serviceAccessCopy}
          </div>
        )}
        {error && (
          <Alert variant="error">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span>{error}</span>
              <Button
                size="sm"
                variant="neutral"
                className="!min-h-11"
                onClick={() => setRetryVersion((value) => value + 1)}
              >
                Spróbuj ponownie
              </Button>
            </div>
          </Alert>
        )}
        {!error && (
          <div className="rs-community-workbench rs-community-workbench--home">
            <div className="min-w-0">
              {loading ? (
                <CommunitySkeletonRows rows={5} />
              ) : (
                <section className="rs-community-list-surface" aria-label="Działy społeczności">
                  <div className="hidden grid-cols-[minmax(0,1fr)_minmax(15rem,0.55fr)] gap-3 border-b border-primary-dark-300/25 px-4 py-2 text-xs font-semibold uppercase tracking-[0.08em] text-primary-light-400 md:grid">
                    <span>Dział</span>
                    <span>Ostatnia aktywność</span>
                  </div>
                  {orderedCategories.map((category) => {
                    const canView = canViewCommunityCategory(
                      category,
                      userProfile,
                      claims,
                      communityProvider
                    );
                    const categoryLatest = latest.find(
                      (thread) => thread.categorySlug === category.slug
                    );
                    const categoryActivityError = activityErrors[category.slug];
                    const hasUnreadActivity = latest.some(
                      (thread) =>
                        thread.categorySlug === category.slug &&
                        isThreadUnread(thread, reads[thread.id], currentUser?.uid)
                    );
                    const rowClass = `rs-community-row rs-community-category-row ${
                      canView ? '' : 'cursor-default opacity-80'
                    } ${hasUnreadActivity ? 'rs-community-row--unread' : ''}`;
                    const rowContent = (
                      <>
                        <div className="min-w-0">
                          <div className="flex min-w-0 items-center gap-2">
                            {category.type === 'service_forum' ? (
                              <WrenchScrewdriverIcon className="h-4 w-4 shrink-0 text-ratuj-300" />
                            ) : category.type === 'announcements' ? (
                              <MegaphoneIcon className="h-4 w-4 shrink-0 text-gold-300" />
                            ) : (
                              <ChatBubbleLeftRightIcon className="h-4 w-4 shrink-0 text-ratuj-300" />
                            )}
                            <h2 className="min-w-0 break-words text-sm font-semibold text-primary-light-100 sm:text-base">
                              {category.title}
                            </h2>
                            {hasUnreadActivity && (
                              <span className="inline-flex shrink-0 items-center gap-1 rounded-md bg-ratuj-300/10 px-2 py-0.5 text-[11px] font-semibold text-ratuj-200">
                                Nowa aktywność
                              </span>
                            )}
                          </div>
                          <p className="mt-0.5 line-clamp-1 text-xs leading-relaxed text-primary-light-300 sm:line-clamp-2">
                            {category.description}
                          </p>
                          <p className="mt-0.5 line-clamp-1 text-xs text-primary-light-500">
                            {canView ? category.writerLabel : 'Dostęp ograniczony'}
                          </p>
                        </div>
                        <div className="pl-6 text-xs text-primary-light-400 md:pl-0">
                          {!canView ? (
                            <span className="block text-primary-light-300">
                              Dział niedostępny dla tej roli
                            </span>
                          ) : categoryActivityError ? (
                            <span className="block text-primary-light-300">
                              {categoryActivityError}
                            </span>
                          ) : categoryLatest ? (
                            <div className="min-w-0">
                              <span className="block truncate font-medium text-primary-light-200">
                                {categoryLatest.title}
                              </span>
                              <span className="mt-0.5 block truncate">
                                {categoryLatest.lastPostAuthorDisplayNameSnapshot ||
                                  categoryLatest.authorDisplayNameSnapshot ||
                                  'Użytkownik'}{' '}
                                · {getVisiblePostsCount(categoryLatest)} wpisy ·{' '}
                                {formatCommunityActivityTime(categoryLatest.lastActivityAt)}
                              </span>
                            </div>
                          ) : (
                            <span className="block truncate">Brak aktywności</span>
                          )}
                        </div>
                      </>
                    );
                    return canView ? (
                      <Link
                        key={category.slug}
                        to={`/app/community/${category.slug}`}
                        className={rowClass}
                      >
                        {rowContent}
                      </Link>
                    ) : (
                      <div key={category.slug} className={rowClass}>
                        {rowContent}
                      </div>
                    );
                  })}
                </section>
              )}
              {!loading && Object.keys(activityErrors).length > 0 && (
                <Button
                  size="sm"
                  variant="neutral"
                  className="mt-3 !min-h-11"
                  onClick={() => setRetryVersion((value) => value + 1)}
                >
                  Ponów nieudane odczyty
                </Button>
              )}
            </div>
            {!loading && latest.length > 0 && (
              <aside
                className="rs-community-context rs-community-desktop-only"
                aria-label="Ostatnia aktywność"
              >
                <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ratuj-300">
                  Ostatnia aktywność
                </p>
                <div className="mt-3 space-y-1">
                  {latest.slice(0, 4).map((thread) => (
                    <Link
                      key={thread.id}
                      to={`/app/community/thread/${thread.id}`}
                      className="rs-community-row block rounded-md px-2 py-2.5"
                    >
                      <span className="block text-xs font-semibold text-primary-light-200">
                        {COMMUNITY_CATEGORY_BY_SLUG.get(thread.categorySlug)?.title ||
                          'Społeczność'}
                      </span>
                      <span className="mt-0.5 block line-clamp-2 break-words text-sm text-primary-light-100">
                        {thread.title}
                      </span>
                      <span className="mt-1 block text-xs text-primary-light-400">
                        {formatCommunityActivityTime(thread.lastActivityAt)}
                      </span>
                    </Link>
                  ))}
                </div>
              </aside>
            )}
          </div>
        )}
        <p className="max-w-4xl text-xs leading-relaxed text-primary-light-400">
          Pisz rzeczowo i bez danych prywatnych. Sprawy konkretnego zgłoszenia prowadź w jego
          czacie.
        </p>
      </div>
    </div>
  );
};

export const CommunityCategoryPage: React.FC = () => {
  const { categorySlug = '' } = useParams();
  const navigate = useNavigate();
  const { currentUser, userProfile, claims } = useAuth();
  const { provider, loadState: providerLoadState } = useCommunityProviderState();
  const communityProvider = providerLoadState === 'ready' ? provider : null;
  const category = COMMUNITY_CATEGORY_BY_SLUG.get(categorySlug);
  const [threads, setThreads] = useState<CommunityThread[]>([]);
  const [reads, setReads] = useState<Record<string, CommunityThreadRead>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadMoreError, setLoadMoreError] = useState<string | null>(null);
  const [cursor, setCursor] = useState<CommunityThreadPageResult['cursor']>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const newThreadEyebrowId = useId();
  const newThreadTitleId = useId();
  const newThreadInitialFocusRef = useRef<HTMLInputElement | null>(null);
  const newThreadTriggerRef = useRef<HTMLButtonElement | null>(null);
  const listRequestVersionRef = useRef(0);
  const [filter, setFilter] = useState<'all' | 'open' | 'closed' | 'pinned'>('all');

  const canView = category
    ? canViewCommunityCategory(category, userProfile, claims, communityProvider)
    : false;
  const canCreate = category
    ? canCreateCommunityThreadInCategory(category, userProfile, claims, communityProvider)
    : false;
  const serviceAccessCopy =
    category?.type === 'service_forum'
      ? getServiceForumAccessCopy(userProfile, claims, providerLoadState, communityProvider)
      : null;
  const availableFilters = useMemo(() => {
    if (category?.type === 'announcements') {
      return [
        { value: 'all' as const, label: 'Wszystkie' },
        { value: 'pinned' as const, label: 'Przypięte' },
        { value: 'closed' as const, label: 'Archiwalne' },
      ];
    }
    return [
      { value: 'all' as const, label: 'Wszystkie' },
      { value: 'open' as const, label: 'Otwarte' },
      { value: 'closed' as const, label: 'Zamknięte' },
      { value: 'pinned' as const, label: 'Przypięte' },
    ];
  }, [category?.type]);

  useEffect(() => {
    if (category?.type === 'announcements' && filter === 'open') {
      setFilter('all');
    }
  }, [category?.type, filter]);

  const loadThreads = useCallback(async () => {
    const requestVersion = ++listRequestVersionRef.current;
    if (!category || !canView) {
      setThreads([]);
      setCursor(null);
      setHasMore(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setLoadMoreError(null);
    try {
      const [result, nextReads] = await Promise.all([
        communityService.getThreadsByCategory(category.slug, { filter }),
        currentUser?.uid ? communityService.getThreadReads(currentUser.uid) : Promise.resolve([]),
      ]);
      if (requestVersion !== listRequestVersionRef.current) return;
      setThreads(result.threads);
      setCursor(result.cursor || null);
      setHasMore(result.hasMore ?? Boolean(result.cursor));
      setReads(
        nextReads.reduce<Record<string, CommunityThreadRead>>((acc, read) => {
          acc[read.threadId] = read;
          return acc;
        }, {})
      );
    } catch (loadError) {
      if (requestVersion !== listRequestVersionRef.current) return;
      console.error('Nie udało się pobrać wątków:', loadError);
      setError(getErrorMessage(loadError, 'load'));
    } finally {
      if (requestVersion === listRequestVersionRef.current) setLoading(false);
    }
  }, [canView, category, currentUser?.uid, filter]);

  useEffect(() => {
    void loadThreads();
  }, [loadThreads]);

  const loadMoreThreads = useCallback(async () => {
    if (!category || !canView || !cursor || loadingMore) return;
    const requestVersion = listRequestVersionRef.current;
    setLoadingMore(true);
    setLoadMoreError(null);
    try {
      const result = await communityService.getThreadsByCategory(category.slug, {
        filter,
        cursor,
      });
      if (requestVersion !== listRequestVersionRef.current) return;
      setThreads((current) => {
        const byId = new Map(current.map((thread) => [thread.id, thread]));
        result.threads.forEach((thread) => byId.set(thread.id, thread));
        return Array.from(byId.values());
      });
      setCursor(result.cursor || null);
      setHasMore(result.hasMore ?? Boolean(result.cursor));
    } catch (loadError) {
      if (requestVersion !== listRequestVersionRef.current) return;
      console.error('Nie udało się pobrać kolejnych wątków:', loadError);
      setLoadMoreError(getErrorMessage(loadError, 'load'));
    } finally {
      if (requestVersion === listRequestVersionRef.current) setLoadingMore(false);
    }
  }, [canView, category, cursor, filter, loadingMore]);

  const openNewThreadForm = (event: React.MouseEvent<HTMLButtonElement>) => {
    newThreadTriggerRef.current = event.currentTarget;
    setShowForm(true);
  };

  if (!category) {
    return (
      <div className={pageShellClass}>
        <div className={innerClass}>
          <div className="rs-community-state">
            <h1 className="text-lg font-semibold text-primary-light-100">Dział nie istnieje</h1>
            <p className="mt-1 text-sm text-primary-light-300">
              Ten adres nie prowadzi do dostępnego działu społeczności.
            </p>
            <Link to="/app/community" className="rs-action-navigation rs-action-md mt-4 w-fit">
              Wróć do Społeczności
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={pageShellClass}>
      <div className={innerClass}>
        <CommunityHeader
          label="Dział"
          title={category.title}
          description={category.description}
          actionsPlacement="top"
          backLink={{ to: '/app/community', label: 'Społeczność' }}
          protectedStyle
          actions={
            canCreate ? (
              <Button
                size="sm"
                onClick={openNewThreadForm}
                icon={<PlusIcon className="h-4 w-4" />}
                className="!min-h-11 w-fit whitespace-nowrap px-3"
              >
                Nowy wątek
              </Button>
            ) : null
          }
        />
        <div
          className={`rs-community-access-strip ${
            canView ? '' : 'rs-community-access-strip--restricted'
          }`}
        >
          {canView
            ? getPermissionStripCopy(category, canCreate, canView)
            : category.type === 'service_forum'
              ? serviceAccessCopy || 'Ta część forum wymaga zatwierdzonego profilu serwisu.'
              : 'Nie masz dostępu do tego działu.'}
          {!canView && category.type === 'service_forum' && isServiceAdmin(userProfile, claims) && (
            <Link
              to="/app/service-profile"
              className="ml-2 inline-flex !min-h-11 items-center font-semibold text-primary-light-100 underline decoration-primary-light-400/50 underline-offset-4 hover:text-ratuj-200"
            >
              Sprawdź profil serwisu
            </Link>
          )}
        </div>
        {!canView ? null : (
          <>
            <Modal
              isOpen={showForm && canCreate}
              onClose={() => setShowForm(false)}
              ariaLabelledBy={`${newThreadEyebrowId} ${newThreadTitleId}`}
              initialFocusRef={newThreadInitialFocusRef}
              returnFocusRef={newThreadTriggerRef}
              placement="bottom-responsive"
              className="rounded-b-none sm:rounded-lg"
            >
              <div className="p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] sm:p-4">
                <div className="mb-3 flex items-start justify-between gap-3">
                  <div>
                    <p
                      id={newThreadEyebrowId}
                      className="text-xs font-semibold uppercase tracking-[0.14em] text-ratuj-300"
                    >
                      Nowy wątek
                    </p>
                    <h2
                      id={newThreadTitleId}
                      className="mt-1 text-lg font-semibold text-primary-light-100"
                    >
                      {category.title}
                    </h2>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="rs-community-utility"
                    aria-label="Zamknij formularz"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
                <ThreadForm
                  category={category}
                  canCreate={canCreate}
                  initialFocusRef={newThreadInitialFocusRef}
                  onCreated={(threadId) => navigate(`/app/community/thread/${threadId}`)}
                />
              </div>
            </Modal>
            <div className="rs-community-filter-bar" role="group" aria-label="Filtr wątków">
              {availableFilters.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  onClick={() => setFilter(item.value)}
                  aria-pressed={filter === item.value}
                  className="rs-community-filter"
                >
                  {item.label}
                </button>
              ))}
            </div>
            {loading ? (
              <CommunitySkeletonRows rows={5} />
            ) : error ? (
              <Alert variant="error">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <span>{error}</span>
                  <Button
                    size="sm"
                    variant="neutral"
                    className="!min-h-11"
                    onClick={() => void loadThreads()}
                  >
                    Spróbuj ponownie
                  </Button>
                </div>
              </Alert>
            ) : threads.length === 0 ? (
              <div className="rs-community-state text-sm">
                <p>
                  {filter !== 'all'
                    ? 'Brak wyników dla wybranego filtra.'
                    : category.type === 'announcements'
                      ? 'Nie ma jeszcze ogłoszeń.'
                      : 'Nie ma jeszcze wątków w tym dziale.'}
                </p>
                {filter !== 'all' ? (
                  <Button size="sm" className="mt-3 !min-h-11" onClick={() => setFilter('all')}>
                    Wyczyść filtr
                  </Button>
                ) : canCreate ? (
                  <Button
                    size="sm"
                    className="mt-3 !min-h-11"
                    onClick={openNewThreadForm}
                    icon={<PlusIcon className="h-4 w-4" />}
                  >
                    Utwórz pierwszy wątek
                  </Button>
                ) : null}
              </div>
            ) : (
              <div className="space-y-3">
                <div className="rs-community-list-surface">
                  <div className="hidden grid-cols-[minmax(0,1fr)_5rem_9rem] gap-2 border-b border-primary-dark-300/25 px-4 py-2 text-xs font-semibold uppercase tracking-[0.08em] text-primary-light-400 md:grid">
                    <span>Wątek</span>
                    <span className="text-right">Wpisy</span>
                    <span className="text-right">Ostatnio</span>
                  </div>
                  {threads.map((thread) => (
                    <ThreadRow
                      key={thread.id}
                      thread={thread}
                      unread={isThreadUnread(thread, reads[thread.id], currentUser?.uid)}
                    />
                  ))}
                </div>
                {loadMoreError && (
                  <Alert variant="error">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <span>{loadMoreError}</span>
                      <Button
                        size="sm"
                        variant="neutral"
                        className="!min-h-11"
                        onClick={() => void loadMoreThreads()}
                      >
                        Ponów
                      </Button>
                    </div>
                  </Alert>
                )}
                {hasMore && (
                  <Button
                    variant="secondary"
                    onClick={() => void loadMoreThreads()}
                    disabled={loadingMore}
                    className="mx-auto !min-h-11"
                  >
                    {loadingMore ? 'Ładowanie…' : 'Pokaż więcej'}
                  </Button>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export const CommunityThreadPage: React.FC = () => {
  const { threadId = '' } = useParams();
  const [searchParams] = useSearchParams();
  const { currentUser, userProfile, claims } = useAuth();
  const { provider, loadState: providerLoadState } = useCommunityProviderState();
  const communityProvider = providerLoadState === 'ready' ? provider : null;
  const [thread, setThread] = useState<CommunityThread | null>(null);
  const [posts, setPosts] = useState<CommunityPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [threadError, setThreadError] = useState<string | null>(null);
  const [postsError, setPostsError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [composerError, setComposerError] = useState<string | null>(null);
  const [composerStatus, setComposerStatus] = useState('');
  const [replyBody, setReplyBody] = useState('');
  const [quotePost, setQuotePost] = useState<CommunityQuoteTarget | null>(null);
  const [replyComposerOpen, setReplyComposerOpen] = useState(false);
  const [expandedPostIds, setExpandedPostIds] = useState<Set<string>>(() => new Set());
  const [submitting, setSubmitting] = useState(false);
  const [reportTarget, setReportTarget] = useState<{ postId?: string } | null>(null);
  const [editingPostId, setEditingPostId] = useState('');
  const [editBody, setEditBody] = useState('');
  const [busyAction, setBusyAction] = useState('');
  const [threadModerationOpen, setThreadModerationOpen] = useState(false);
  const [highlightedPostId, setHighlightedPostId] = useState<string | null>(null);
  const [targetPostMissing, setTargetPostMissing] = useState(false);
  const [moderationConfirmation, setModerationConfirmation] = useState<{
    action: 'hide_thread' | 'hide_post';
    postId?: string;
    title: string;
    message: string;
    confirmLabel: string;
  } | null>(null);
  const replyBodyId = useId();
  const replyErrorId = useId();
  const moderationTitleId = useId();
  const composerRef = useRef<HTMLFormElement | null>(null);
  const replyTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const replySubmitInFlightRef = useRef(false);
  const moderationPendingRef = useRef<Set<string>>(new Set());
  const composerTriggerRef = useRef<HTMLElement | null>(null);
  const reportReturnFocusRef = useRef<HTMLElement | null>(null);
  const editReturnFocusRef = useRef<HTMLElement | null>(null);
  const editTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const moderationReturnFocusRef = useRef<HTMLElement | null>(null);
  const moderationCancelRef = useRef<HTMLButtonElement | null>(null);
  const threadModerationButtonRef = useRef<HTMLButtonElement | null>(null);
  const readbackNotificationIdsRef = useRef<Set<string>>(new Set());
  const replyClientRequestIdRef = useRef<string | null>(null);
  const replyRequestPayloadRef = useRef<string | null>(null);
  const communityRole = userProfile?.role;
  const communityStatus = userProfile?.status;
  const hasCommunityClaims = Boolean(claims);
  const communityClaimIsAdmin = claims?.isAdmin === true;
  const communityClaimIsServiceAdmin = claims?.isServiceAdmin === true;
  const communityClaimIsServiceProvider = claims?.isServiceProvider === true;
  const communityAccessProfile = useMemo(
    () =>
      communityRole || communityStatus
        ? {
            role: communityRole,
            status: communityStatus,
          }
        : null,
    [communityRole, communityStatus]
  );
  const communityAccessClaims = useMemo(
    () =>
      hasCommunityClaims
        ? {
            isAdmin: communityClaimIsAdmin,
            isServiceAdmin: communityClaimIsServiceAdmin,
            isServiceProvider: communityClaimIsServiceProvider,
          }
        : null,
    [
      communityClaimIsAdmin,
      communityClaimIsServiceAdmin,
      communityClaimIsServiceProvider,
      hasCommunityClaims,
    ]
  );
  const admin = isActiveCommunityAdmin(communityAccessProfile, communityAccessClaims);
  const targetPostId = searchParams.get('postId')?.trim() || '';
  const notificationId = searchParams.get('notificationId')?.trim() || '';
  const postsReadFailed = Boolean(postsError);
  const loadThread = useCallback(
    async (options: { preserveContent?: boolean } = {}): Promise<CommunityPost[] | null> => {
      if (!threadId) return null;
      if (!options.preserveContent) {
        setLoading(true);
      }
      setThreadError(null);
      setPostsError(null);
      try {
        const nextThread = await communityService.getThread(threadId);
        setThread(nextThread);
        if (!nextThread) {
          setPosts([]);
          setThreadError('Nie znaleziono wątku.');
          return null;
        }
        if (
          !canViewCommunityThread(
            communityAccessProfile,
            communityAccessClaims,
            nextThread,
            communityProvider
          )
        ) {
          setPosts([]);
          setThreadError('Nie masz dostępu do tego wątku.');
          return null;
        }
        let nextPosts: CommunityPost[] = [];
        try {
          nextPosts = await communityService.getPosts(threadId, { includeModerated: admin });
        } catch (postsError) {
          console.error('Nie udało się pobrać odpowiedzi wątku:', postsError);
          setPosts([]);
          setPostsError(getErrorMessage(postsError, 'posts'));
          return null;
        }
        setPosts(nextPosts);
        setTargetPostMissing(
          Boolean(
            targetPostId &&
              !nextPosts.some((post) => post.id === targetPostId && post.status === 'visible')
          )
        );
        void communityService
          .markThreadRead({
            threadId,
            categorySlug: nextThread.categorySlug,
            lastSeenPostId: nextPosts[nextPosts.length - 1]?.id,
          })
          .catch((readError) => {
            console.error('Nie udało się oznaczyć wątku jako przeczytanego:', readError);
          });
        return nextPosts;
      } catch (loadError) {
        console.error('Nie udało się pobrać wątku:', loadError);
        setThread(null);
        setPosts([]);
        setThreadError(getErrorMessage(loadError, 'thread'));
        return null;
      } finally {
        if (!options.preserveContent) {
          setLoading(false);
        }
      }
    },
    [
      admin,
      communityAccessClaims,
      communityAccessProfile,
      communityProvider,
      targetPostId,
      threadId,
    ]
  );

  useEffect(() => {
    void loadThread();
  }, [loadThread]);

  useEffect(() => {
    setExpandedPostIds(new Set());
    setHighlightedPostId(null);
    setTargetPostMissing(false);
    replyClientRequestIdRef.current = null;
    replyRequestPayloadRef.current = null;
  }, [threadId]);

  useEffect(() => {
    if (loading || threadError || postsReadFailed || !thread || targetPostMissing) return;
    const targetPost = targetPostId
      ? posts.find((post) => post.id === targetPostId && post.status === 'visible')
      : null;
    if (targetPostId && !targetPost) return;
    const targetElement = targetPostId
      ? document.getElementById(`community-post-${targetPostId}`)
      : null;
    if (targetPostId && !targetElement) return;

    let highlightTimeoutId: number | null = null;
    const targetTimeoutId = window.setTimeout(
      () => {
        if (targetElement) {
          const reduceMotion =
            window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
          targetElement.scrollIntoView({
            behavior: reduceMotion ? 'auto' : 'smooth',
            block: 'start',
          });
          targetElement.focus({ preventScroll: true });
          setHighlightedPostId(targetPostId);
          highlightTimeoutId = window.setTimeout(() => {
            setHighlightedPostId((current) => (current === targetPostId ? null : current));
          }, 2800);
        }

        if (notificationId && !readbackNotificationIdsRef.current.has(notificationId)) {
          readbackNotificationIdsRef.current.add(notificationId);
          void notificationService
            .markCommunityNotificationAsRead({
              notificationId,
              threadId,
              postId: targetPostId || null,
            })
            .catch((readError) => {
              readbackNotificationIdsRef.current.delete(notificationId);
              console.error('Nie udało się oznaczyć powiadomienia jako przeczytanego:', readError);
            });
        }
      },
      targetElement ? 80 : 0
    );

    return () => {
      window.clearTimeout(targetTimeoutId);
      if (highlightTimeoutId !== null) window.clearTimeout(highlightTimeoutId);
    };
  }, [
    loading,
    notificationId,
    posts,
    postsReadFailed,
    targetPostId,
    targetPostMissing,
    thread,
    threadError,
    threadId,
  ]);

  const replyAllowed = thread
    ? canReplyToCommunityThread(
        thread,
        currentUser?.uid,
        communityAccessProfile,
        communityAccessClaims,
        communityProvider
      )
    : false;
  const category = thread ? COMMUNITY_CATEGORY_BY_SLUG.get(thread.categorySlug) : null;
  const categoryReturn = category
    ? { to: `/app/community/${category.slug}`, label: category.title }
    : { to: '/app/community', label: 'Społeczność' };
  const approvedService = isApprovedCommunityServiceAdmin(
    communityAccessProfile,
    communityAccessClaims,
    communityProvider
  );
  const replyCtaLabel = 'Odpowiedz';
  const replyIdentity = String(
    (userProfile as { displayName?: unknown })?.displayName ||
      currentUser?.displayName ||
      (approvedService ? 'serwis' : 'użytkownik')
  );
  const displayPosts = posts;
  const hasCanonicalOpeningPost = useMemo(
    () => !postsReadFailed && posts.some((post) => post.isOpeningPost),
    [posts, postsReadFailed]
  );
  const threadPreview = hasCanonicalOpeningPost ? '' : normalizeCommunityText(thread?.bodyExcerpt);
  const threadMeta = thread
    ? `${getVisiblePostsCount(thread)} wpisy · Ostatnio ${
        thread.lastPostAuthorDisplayNameSnapshot || thread.authorDisplayNameSnapshot || 'Użytkownik'
      } · ${formatCommunityActivityTime(thread.lastActivityAt)}`
    : '';

  const openReplyComposer = useCallback(
    (nextQuotePost?: CommunityPost | null, trigger?: HTMLElement | null) => {
      if (trigger) composerTriggerRef.current = trigger;
      setComposerError(null);
      setComposerStatus('');
      setQuotePost(
        nextQuotePost
          ? {
              id: nextQuotePost.id,
              authorDisplayNameSnapshot: nextQuotePost.authorDisplayNameSnapshot,
              body: nextQuotePost.body,
            }
          : null
      );
      setReplyComposerOpen(true);
      window.setTimeout(() => {
        const reduceMotion =
          window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
        composerRef.current?.scrollIntoView({
          behavior: reduceMotion ? 'auto' : 'smooth',
          block: 'start',
        });
        replyTextareaRef.current?.focus({ preventScroll: true });
      }, 0);
    },
    []
  );

  const closeReplyComposer = useCallback(() => {
    setReplyComposerOpen(false);
    setQuotePost(null);
    setComposerError(null);
    window.setTimeout(() => composerTriggerRef.current?.focus(), 0);
  }, []);

  const togglePostExpanded = (postId: string) => {
    setExpandedPostIds((current) => {
      const next = new Set(current);
      if (next.has(postId)) {
        next.delete(postId);
      } else {
        next.add(postId);
      }
      return next;
    });
  };

  const submitReply = async (event: FormEvent) => {
    event.preventDefault();
    if (!thread) return;
    if (replySubmitInFlightRef.current) return;
    if (!replyAllowed) {
      setComposerError('Nie masz uprawnień do odpowiedzi w tym wątku.');
      return;
    }
    if (replyBody.trim().length < 2) {
      setComposerError('Treść odpowiedzi musi mieć co najmniej 2 znaki.');
      replyTextareaRef.current?.focus();
      return;
    }
    replySubmitInFlightRef.current = true;
    setSubmitting(true);
    setComposerError(null);
    setComposerStatus('');
    try {
      const normalizedBody = replyBody.trim();
      const quotePostId = quotePost?.id || '';
      const requestPayload = JSON.stringify({
        threadId: thread.id,
        body: normalizedBody,
        quotePostId,
      });
      if (!replyClientRequestIdRef.current || replyRequestPayloadRef.current !== requestPayload) {
        replyClientRequestIdRef.current = createCommunityClientRequestId();
        replyRequestPayloadRef.current = requestPayload;
      }
      const result = await communityService.createPost({
        threadId: thread.id,
        body: normalizedBody,
        clientRequestId: replyClientRequestIdRef.current,
        quotePostId: quotePostId || undefined,
      });
      const nextPosts = await loadThread({ preserveContent: true });
      const createdPostWasReadBack = Boolean(nextPosts?.some((post) => post.id === result.postId));
      if (!createdPostWasReadBack) {
        setComposerError(
          'Odpowiedź została zapisana, ale nie udało się jeszcze potwierdzić jej na liście. Odśwież wpisy.'
        );
        return;
      }
      setReplyBody('');
      setQuotePost(null);
      setReplyComposerOpen(false);
      replyClientRequestIdRef.current = null;
      replyRequestPayloadRef.current = null;
      setComposerStatus('Odpowiedź została opublikowana.');
      window.setTimeout(() => {
        document.getElementById(`community-post-${result.postId}`)?.focus({ preventScroll: false });
      }, 0);
    } catch (submitError) {
      setComposerError(
        getFriendlyFirebaseRuntimeError(submitError, getErrorMessage(submitError, 'reply'))
      );
    } finally {
      replySubmitInFlightRef.current = false;
      setSubmitting(false);
    }
  };

  const saveEdit = async (post: CommunityPost) => {
    setBusyAction(`edit-${post.id}`);
    setActionError(null);
    try {
      await communityService.editContent({
        threadId: post.threadId,
        postId: post.id,
        body: editBody,
      });
      setEditingPostId('');
      setEditBody('');
      await loadThread({ preserveContent: true });
    } catch (editError) {
      setActionError(getFriendlyFirebaseRuntimeError(editError, getErrorMessage(editError)));
    } finally {
      setBusyAction('');
    }
  };

  const moderate = async (
    action: Parameters<typeof communityService.moderate>[0]['action'],
    postId?: string
  ): Promise<boolean> => {
    if (!thread) return false;
    const pendingKey = `${action}-${postId || thread.id}`;
    if (moderationPendingRef.current.has(pendingKey)) return false;
    moderationPendingRef.current.add(pendingKey);
    setBusyAction(pendingKey);
    setActionError(null);
    try {
      await communityService.moderate({ action, threadId: thread.id, postId });
      await loadThread({ preserveContent: true });
      return true;
    } catch (moderationError) {
      setActionError(
        getFriendlyFirebaseRuntimeError(moderationError, getErrorMessage(moderationError))
      );
      return false;
    } finally {
      moderationPendingRef.current.delete(pendingKey);
      setBusyAction('');
    }
  };

  const requestModeration = (
    action: Parameters<typeof communityService.moderate>[0]['action'],
    postId?: string,
    returnFocusElement?: HTMLElement | null
  ) => {
    moderationReturnFocusRef.current = returnFocusElement || null;
    if (action === 'hide_thread') {
      setModerationConfirmation({
        action,
        title: 'Ukryć wątek?',
        message: 'Wątek przestanie być widoczny dla zwykłych uczestników społeczności.',
        confirmLabel: 'Ukryj wątek',
      });
      return;
    }
    if (action === 'hide_post' && postId) {
      setModerationConfirmation({
        action,
        postId,
        title: 'Ukryć wpis?',
        message: 'Wpis przestanie być widoczny dla zwykłych uczestników wątku.',
        confirmLabel: 'Ukryj wpis',
      });
      return;
    }
    void moderate(action, postId);
  };

  useEffect(() => {
    if (!editingPostId) return;
    window.setTimeout(() => editTextareaRef.current?.focus(), 0);
  }, [editingPostId]);

  if (loading) {
    return (
      <div className={pageShellClass}>
        <div className={innerClass}>
          <div className="rs-community-state p-6 text-center" aria-label="Ładowanie wątku">
            <Spinner className="mx-auto" />
          </div>
        </div>
      </div>
    );
  }

  if (threadError && !thread) {
    return (
      <div className={pageShellClass}>
        <div className={innerClass}>
          <div className="rs-community-state">
            <h1 className="text-lg font-semibold text-primary-light-100">
              Nie udało się otworzyć wątku
            </h1>
            <p className="mt-1 text-sm text-primary-light-300">{threadError}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button className="!min-h-11" onClick={() => void loadThread()}>
                Spróbuj ponownie
              </Button>
              <Link to="/app/community" className="rs-action-navigation rs-action-md">
                Wróć do Społeczności
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!thread) {
    return (
      <div className={pageShellClass}>
        <div className={innerClass}>
          <Alert variant="error">Wątek nie istnieje albo nie masz dostępu.</Alert>
        </div>
      </div>
    );
  }

  return (
    <div className={pageShellClass}>
      <div className={`${innerClass} rs-community-shell--reading`}>
        <CommunityHeader
          label="Wątek"
          title={thread.title}
          description={threadPreview}
          actionsPlacement="top"
          backLink={categoryReturn}
          protectedStyle
          status={
            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-primary-light-400">
              <span>{threadMeta}</span>
              <CommunityStatusChips thread={thread} />
            </div>
          }
          actions={
            admin ? (
              <>
                <button
                  ref={threadModerationButtonRef}
                  type="button"
                  aria-label="Moderuj wątek"
                  aria-expanded={threadModerationOpen}
                  aria-haspopup="menu"
                  onClick={() => setThreadModerationOpen((value) => !value)}
                  className="rs-community-utility"
                >
                  <EllipsisVerticalIcon className="h-5 w-5" />
                </button>
                <ViewportDropdown
                  open={threadModerationOpen}
                  anchorRef={threadModerationButtonRef}
                  onClose={() => setThreadModerationOpen(false)}
                >
                  <MenuItemButton
                    onClick={() => {
                      setThreadModerationOpen(false);
                      requestModeration(
                        thread.isPinned ? 'unpin_thread' : 'pin_thread',
                        undefined,
                        threadModerationButtonRef.current
                      );
                    }}
                  >
                    {thread.isPinned ? 'Odepnij wątek' : 'Przypnij wątek'}
                  </MenuItemButton>
                  <MenuItemButton
                    onClick={() => {
                      setThreadModerationOpen(false);
                      requestModeration(
                        thread.status === 'closed' ? 'reopen_thread' : 'close_thread',
                        undefined,
                        threadModerationButtonRef.current
                      );
                    }}
                  >
                    {thread.status === 'closed' ? 'Otwórz wątek' : 'Zamknij wątek'}
                  </MenuItemButton>
                  <MenuItemButton
                    destructive={thread.status !== 'hidden'}
                    onClick={() => {
                      setThreadModerationOpen(false);
                      requestModeration(
                        thread.status === 'hidden' ? 'restore_thread' : 'hide_thread',
                        undefined,
                        threadModerationButtonRef.current
                      );
                    }}
                  >
                    {thread.status === 'hidden' ? 'Przywróć wątek' : 'Ukryj wątek'}
                  </MenuItemButton>
                </ViewportDropdown>
              </>
            ) : null
          }
        />
        {threadError && <Alert variant="error">{threadError}</Alert>}
        {actionError && <Alert variant="error">{actionError}</Alert>}
        {postsError && (
          <Alert variant="error">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span>{postsError}</span>
              <Button
                size="sm"
                variant="neutral"
                className="!min-h-11"
                onClick={() => void loadThread({ preserveContent: true })}
              >
                Odśwież wpisy
              </Button>
            </div>
          </Alert>
        )}
        {targetPostMissing && !postsReadFailed && (
          <Alert variant="info">
            Nie znaleziono wskazanej odpowiedzi. Mogła zostać ukryta albo usunięta.
          </Alert>
        )}
        <div className="rs-community-workbench rs-community-workbench--thread">
          <div className="min-w-0 space-y-3">
            {!postsReadFailed && (
              <section className="rs-community-conversation" aria-label="Rozmowa">
                {displayPosts.length === 0 ? (
                  <div className="p-4 text-sm text-primary-light-300">Brak wpisów.</div>
                ) : (
                  displayPosts.map((post) => (
                    <article
                      key={post.id}
                      id={`community-post-${post.id}`}
                      tabIndex={-1}
                      className={`rs-community-post ${
                        post.status !== 'visible'
                          ? 'rs-community-post--moderated'
                          : post.isOpeningPost
                            ? 'rs-community-post--opening'
                            : ''
                      } ${highlightedPostId === post.id ? 'rs-community-post--target' : ''}`}
                    >
                      <div className="flex min-w-0 items-start gap-2.5 xl:block">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary-dark-100/55 text-[11px] font-semibold text-primary-light-200 xl:mb-2">
                          {getAuthorInitials(post.authorDisplayNameSnapshot)}
                        </span>
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm xl:block">
                            <span className="block max-w-[14rem] truncate font-semibold text-primary-light-100 xl:max-w-full">
                              {post.authorDisplayNameSnapshot}
                            </span>
                            <span className="xl:mt-1 xl:block">
                              <AuthorRoleChip
                                role={post.authorRoleSnapshot}
                                categoryType={category?.type}
                              />
                            </span>
                            <span className="text-xs text-primary-light-400 xl:mt-1.5 xl:block">
                              {formatCommunityDate(post.createdAt)}
                            </span>
                            {post.editedAt && (
                              <span className="text-xs text-primary-light-400 xl:block">
                                edytowano
                              </span>
                            )}
                            {admin && post.status !== 'visible' && (
                              <span className="xl:mt-1 xl:block">
                                <Badge variant="error" size="sm">
                                  Ukryty
                                </Badge>
                              </span>
                            )}
                          </div>
                          {post.isOpeningPost && (
                            <span className="mt-1 block text-[11px] font-semibold uppercase tracking-[0.08em] text-primary-light-500">
                              {getOpeningPostLabel(category?.type)}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="rs-community-post-body min-w-0">
                        <div className="mb-2 flex !min-h-11 items-center justify-end gap-1">
                          {replyAllowed &&
                            !post.isServiceGratitudeReply &&
                            category?.type !== 'gratitude' &&
                            post.status === 'visible' && (
                              <button
                                type="button"
                                onClick={(event) => openReplyComposer(post, event.currentTarget)}
                                className="rs-community-utility !min-w-0 px-2 text-xs font-semibold hover:text-ratuj-200"
                              >
                                <ArrowUturnLeftIcon className="h-3.5 w-3.5" />
                                Cytuj
                              </button>
                            )}
                          <PostActionMenu
                            post={post}
                            canEdit={
                              isOwn(currentUser?.uid, post.authorId) &&
                              post.status === 'visible' &&
                              thread.status === 'open'
                            }
                            admin={admin}
                            busyAction={busyAction}
                            onEdit={(returnFocusElement) => {
                              editReturnFocusRef.current = returnFocusElement;
                              setEditingPostId(post.id);
                              setEditBody(post.body);
                            }}
                            onReport={(returnFocusElement) => {
                              reportReturnFocusRef.current = returnFocusElement;
                              setReportTarget({ postId: post.id });
                            }}
                            onModerate={(action, postId, returnFocusElement) =>
                              requestModeration(action, postId, returnFocusElement)
                            }
                          />
                        </div>
                        {post.status !== 'visible' && !admin ? (
                          <p className="max-w-[52rem] text-sm text-primary-light-400">
                            Ta odpowiedź została ukryta przez moderatora.
                          </p>
                        ) : editingPostId === post.id ? (
                          <div className="max-w-[52rem] space-y-2">
                            <label
                              htmlFor={`community-edit-${post.id}`}
                              className="block text-sm font-semibold text-primary-light-200"
                            >
                              Treść wpisu
                            </label>
                            <textarea
                              id={`community-edit-${post.id}`}
                              ref={editTextareaRef}
                              value={editBody}
                              onChange={(event) => setEditBody(event.target.value)}
                              className="rs-community-input min-h-[8rem] resize-y text-sm"
                            />
                            <div className="flex flex-wrap gap-2">
                              <Button
                                size="sm"
                                className="!min-h-11"
                                onClick={() => saveEdit(post)}
                                isLoading={busyAction === `edit-${post.id}`}
                              >
                                Zapisz
                              </Button>
                              <Button
                                size="sm"
                                variant="neutral"
                                className="!min-h-11"
                                onClick={() => {
                                  setEditingPostId('');
                                  window.setTimeout(() => editReturnFocusRef.current?.focus(), 0);
                                }}
                              >
                                Anuluj
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="max-w-[52rem] space-y-2.5">
                            {post.quote && (
                              <blockquote className="rs-community-quote text-xs leading-relaxed">
                                <span className="block font-medium text-primary-light-300">
                                  {post.quote.quotedUnavailable
                                    ? 'Cytowany wpis'
                                    : `Cytat: ${post.quote.quotedAuthorDisplayNameSnapshot}`}
                                </span>
                                <span className="mt-0.5 block line-clamp-2">
                                  {post.quote.quotedExcerpt}
                                </span>
                              </blockquote>
                            )}
                            <ExpandableText
                              text={post.body}
                              expanded={expandedPostIds.has(post.id)}
                              onToggle={() => togglePostExpanded(post.id)}
                              collapsedLines={post.isOpeningPost ? 4 : 3}
                              className="text-primary-light-200/90"
                            />
                          </div>
                        )}
                      </div>
                    </article>
                  ))
                )}
              </section>
            )}
            <p aria-live="polite" role="status" className="sr-only">
              {composerStatus}
            </p>
            {postsReadFailed && replyAllowed ? (
              <div className="rs-community-access-strip rs-community-access-strip--restricted">
                Najpierw odśwież wpisy, żeby odpowiedzieć z pełnym kontekstem wątku.
              </div>
            ) : replyAllowed && !replyComposerOpen ? (
              <div className="rs-community-composer flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <h2 className="text-sm font-semibold text-primary-light-100">
                    Dołącz do rozmowy
                  </h2>
                  <p className="mt-0.5 text-xs leading-relaxed text-primary-light-400">
                    Piszesz jako: {replyIdentity}
                  </p>
                </div>
                <Button
                  className="!min-h-11 w-fit self-start sm:self-auto"
                  onClick={(event) => openReplyComposer(null, event.currentTarget)}
                >
                  {replyCtaLabel}
                </Button>
              </div>
            ) : replyAllowed ? (
              <form
                ref={composerRef}
                onSubmit={submitReply}
                noValidate
                className="rs-community-composer space-y-3"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h2 className="text-base font-semibold text-primary-light-100">Odpowiedź</h2>
                    <p className="mt-0.5 truncate text-xs text-primary-light-400">
                      Piszesz jako: {replyIdentity}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={closeReplyComposer}
                    className="rs-community-utility"
                    aria-label="Zamknij formularz odpowiedzi"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
                {composerError && (
                  <div role="alert">
                    <Alert variant="error" className="scroll-mt-4">
                      <span id={replyErrorId}>{composerError}</span>
                    </Alert>
                  </div>
                )}
                {quotePost && (
                  <div className="rs-community-quote flex items-start justify-between gap-3 text-xs leading-relaxed">
                    <div className="min-w-0">
                      <span className="block font-medium text-primary-light-300">
                        Cytujesz: {quotePost.authorDisplayNameSnapshot}
                      </span>
                      <span className="line-clamp-2">{quotePost.body}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setQuotePost(null);
                        window.setTimeout(() => replyTextareaRef.current?.focus(), 0);
                      }}
                      className="rs-community-utility"
                      aria-label="Usuń cytat"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                )}
                <div className="space-y-1.5">
                  <div className="flex items-baseline justify-between gap-3">
                    <label
                      htmlFor={replyBodyId}
                      className="block text-sm font-semibold text-primary-light-200"
                    >
                      Treść odpowiedzi
                    </label>
                    <span className="text-xs text-primary-light-400">
                      {replyBody.length} znaków
                    </span>
                  </div>
                  <textarea
                    id={replyBodyId}
                    ref={replyTextareaRef}
                    value={replyBody}
                    onChange={(event) => {
                      setReplyBody(event.target.value);
                      if (composerError) setComposerError(null);
                    }}
                    rows={5}
                    maxLength={category?.type === 'gratitude' ? 800 : 5000}
                    className="rs-community-input min-h-[8rem] resize-y text-sm leading-relaxed placeholder-primary-light-400"
                    placeholder="Napisz rzeczową odpowiedź"
                    aria-invalid={Boolean(composerError)}
                    aria-describedby={composerError ? replyErrorId : undefined}
                    required
                  />
                </div>
                <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end">
                  <Button
                    type="button"
                    variant="neutral"
                    className="!min-h-11"
                    onClick={closeReplyComposer}
                  >
                    Anuluj
                  </Button>
                  <Button
                    type="submit"
                    isLoading={submitting}
                    disabled={!replyAllowed}
                    className="!min-h-11"
                  >
                    Opublikuj
                  </Button>
                </div>
              </form>
            ) : thread.status === 'closed' ? (
              <div className="rs-community-access-strip rs-community-access-strip--restricted">
                Wątek zamknięty. Nie można dodać odpowiedzi.
              </div>
            ) : (
              <div className="rs-community-access-strip rs-community-access-strip--restricted">
                Możesz czytać ten dział, ale tworzenie treści zależy od roli i zatwierdzenia.
              </div>
            )}
          </div>
          <aside
            className="rs-community-context rs-community-thread-context rs-community-desktop-only"
            aria-label="Kontekst wątku"
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ratuj-300">
              Kontekst
            </p>
            <dl className="mt-3 space-y-3 text-xs">
              <div>
                <dt className="text-primary-light-400">Dział</dt>
                <dd className="mt-0.5 font-semibold text-primary-light-200">
                  {category?.title || 'Społeczność'}
                </dd>
              </div>
              <div>
                <dt className="text-primary-light-400">Stan rozmowy</dt>
                <dd className="mt-0.5 text-primary-light-200">
                  {thread.status === 'open'
                    ? 'Otwarta'
                    : thread.status === 'closed'
                      ? 'Zamknięta'
                      : 'Ukryta'}
                </dd>
              </div>
              <div>
                <dt className="text-primary-light-400">Twoja możliwość</dt>
                <dd className="mt-0.5 text-primary-light-200">
                  {replyAllowed ? 'Możesz odpowiedzieć' : 'Tylko odczyt'}
                </dd>
              </div>
            </dl>
            {replyAllowed && !postsReadFailed && (
              <Button
                variant="secondary"
                className="mt-4 !min-h-11 w-full"
                aria-label="Odpowiedz i przejdź do formularza"
                onClick={(event) => openReplyComposer(null, event.currentTarget)}
              >
                {replyCtaLabel}
              </Button>
            )}
          </aside>
        </div>
        <ReportDialog
          isOpen={reportTarget !== null}
          onClose={() => setReportTarget(null)}
          threadId={thread.id}
          postId={reportTarget?.postId}
          returnFocusRef={reportReturnFocusRef}
        />
        <Modal
          isOpen={moderationConfirmation !== null}
          onClose={() => setModerationConfirmation(null)}
          ariaLabelledBy={moderationTitleId}
          initialFocusRef={moderationCancelRef}
          returnFocusRef={moderationReturnFocusRef}
        >
          <div className="space-y-4 p-4">
            <div>
              <h2 id={moderationTitleId} className="text-base font-semibold text-primary-light-100">
                {moderationConfirmation?.title}
              </h2>
              <p className="mt-1 text-sm leading-relaxed text-primary-light-300">
                {moderationConfirmation?.message}
              </p>
            </div>
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button
                ref={moderationCancelRef}
                type="button"
                variant="neutral"
                className="!min-h-11"
                onClick={() => setModerationConfirmation(null)}
              >
                Anuluj
              </Button>
              <Button
                type="button"
                variant="error"
                className="!min-h-11"
                isLoading={Boolean(
                  moderationConfirmation &&
                    busyAction ===
                      `${moderationConfirmation.action}-${
                        moderationConfirmation.postId || thread.id
                      }`
                )}
                onClick={async () => {
                  if (!moderationConfirmation) return;
                  const completed = await moderate(
                    moderationConfirmation.action,
                    moderationConfirmation.postId
                  );
                  if (completed) setModerationConfirmation(null);
                }}
              >
                {moderationConfirmation?.confirmLabel || 'Potwierdź'}
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
};

type AdminModerationPayload = Parameters<typeof communityService.moderate>[0];
type AdminThreadCommand = Exclude<
  AdminModerationPayload['action'],
  'hide_post' | 'restore_post' | 'resolve_report'
>;

type AdminModerationConfirmation = {
  kind: 'report' | 'thread';
  targetKey: string;
  title: string;
  message: string;
  confirmLabel: string;
  payload: AdminModerationPayload;
  successMessage: string;
};

const ADMIN_COMMUNITY_COMPACT_QUERY = '(max-width: 1023px)';

const useCompactAdminCommunity = (): boolean => {
  const [compact, setCompact] = useState(() =>
    typeof window === 'undefined' ? false : window.matchMedia(ADMIN_COMMUNITY_COMPACT_QUERY).matches
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mediaQuery = window.matchMedia(ADMIN_COMMUNITY_COMPACT_QUERY);
    const update = () => setCompact(mediaQuery.matches);
    update();
    mediaQuery.addEventListener?.('change', update);
    return () => mediaQuery.removeEventListener?.('change', update);
  }, []);

  return compact;
};

const getAdminCommunityError = (error: unknown): string =>
  getFriendlyFirebaseRuntimeError(error, getErrorMessage(error));

const getReportReasonLabel = (reason: CommunityReportReason): string =>
  COMMUNITY_REPORT_REASONS.find((item) => item.value === reason)?.label || reason;

const getAdminThreadStatusLabel = (thread: CommunityThread): string => {
  if (thread.status === 'hidden') return 'Ukryty';
  if (thread.status === 'deleted') return 'Usunięty';
  if (thread.status === 'closed') return 'Zamknięty';
  return 'Otwarty';
};

const getAdminThreadCategoryLabel = (thread: CommunityThread): string =>
  COMMUNITY_CATEGORY_BY_SLUG.get(thread.categorySlug)?.title || thread.categorySlug || 'Bez działu';

const AdminCommunityRowSkeleton: React.FC<{ label: string }> = ({ label }) => (
  <div className="rs-admin-community-row-skeleton" role="status" aria-label={label}>
    <span className="rs-skeleton h-3 w-24" />
    <span className="rs-skeleton h-4 w-4/5" />
    <span className="rs-skeleton h-3 w-3/5" />
  </div>
);

const AdminReportInspector: React.FC<{
  report: CommunityReport | null;
  thread: CommunityThread | null;
  pending: boolean;
  error: string | null;
  onResolve: (status: 'dismissed' | 'action_taken', trigger: HTMLButtonElement) => void;
}> = ({ report, thread, pending, error, onResolve }) => {
  if (!report) {
    return (
      <div className="rs-admin-community-inspector-empty">
        <p className="text-sm font-semibold text-primary-light-100">Wybierz zgłoszenie</p>
        <p className="mt-1 text-sm leading-relaxed text-primary-light-400">
          Inspektor pokaże tylko kontekst potrzebny do decyzji. Pełna rozmowa pozostaje w chronionym
          wątku.
        </p>
      </div>
    );
  }

  const targetLabel = report.targetType === 'post' ? 'Wpis' : 'Wątek';
  const targetTitle = thread?.title?.trim() || 'Wątek niedostępny w załadowanym zestawie';

  return (
    <div className="min-w-0" data-testid="admin-report-inspector">
      <div className="rs-admin-community-inspector-heading">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ratuj-300">
            {targetLabel} · zgłoszenie otwarte
          </p>
          <h3 className="mt-1 break-words text-base font-semibold text-primary-light-100">
            {targetTitle}
          </h3>
        </div>
      </div>

      <dl className="rs-admin-community-facts">
        <div>
          <dt>Powód</dt>
          <dd>{getReportReasonLabel(report.reason)}</dd>
        </div>
        <div>
          <dt>Data zgłoszenia</dt>
          <dd>{formatCommunityDate(report.createdAt)}</dd>
        </div>
        <div>
          <dt>Zgłaszający</dt>
          <dd className="break-all">
            {getCommunityRoleLabel(report.reporterRoleSnapshot)} · {report.reportedBy || 'brak ID'}
          </dd>
        </div>
        <div>
          <dt>Target</dt>
          <dd className="break-all">
            {report.targetType} · {report.postId || report.threadId}
          </dd>
        </div>
        <div>
          <dt>Stan wątku</dt>
          <dd>{thread ? getAdminThreadStatusLabel(thread) : 'Brak targetu w bieżącym odczycie'}</dd>
        </div>
        {thread && (
          <div>
            <dt>Dział</dt>
            <dd>{getAdminThreadCategoryLabel(thread)}</dd>
          </div>
        )}
      </dl>

      {report.message ? (
        <div className="rs-admin-community-note">
          <p className="text-xs font-semibold text-primary-light-300">Notatka zgłaszającego</p>
          <p className="mt-1 whitespace-pre-wrap break-words text-sm leading-relaxed text-primary-light-200">
            {report.message}
          </p>
        </div>
      ) : (
        <p className="mt-3 text-xs text-primary-light-400">
          Zgłoszenie nie zawiera dodatkowej notatki.
        </p>
      )}

      {thread?.bodyExcerpt && (
        <div className="rs-admin-community-note">
          <p className="text-xs font-semibold text-primary-light-300">Kontekst wątku</p>
          <p className="mt-1 line-clamp-3 break-words text-sm leading-relaxed text-primary-light-300">
            {thread.bodyExcerpt}
          </p>
        </div>
      )}

      {report.targetType === 'post' && (
        <p className="mt-3 text-xs leading-relaxed text-primary-light-400">
          Stan wpisu nie należy do bieżącego read modelu tego widoku. Inspektor nie pobiera feedu
          ani nie udostępnia akcji wpisu bez potwierdzonego stanu.
        </p>
      )}

      {error && (
        <div role="alert" className="mt-3">
          <Alert variant="error">{error}</Alert>
        </div>
      )}

      <div className="rs-admin-community-decision">
        <div>
          <p className="text-sm font-semibold text-primary-light-100">Decyzja o zgłoszeniu</p>
          <p className="mt-1 text-xs leading-relaxed text-primary-light-400">
            Rozstrzygnięcie nie ukryje, nie zamknie ani nie przypnie treści.
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
          <Button
            type="button"
            disabled={pending}
            isLoading={pending}
            className="!min-h-11"
            onClick={(event) => onResolve('dismissed', event.currentTarget)}
          >
            Oddal zgłoszenie
          </Button>
          <Button
            type="button"
            variant="neutral"
            disabled={pending}
            className="!min-h-11"
            onClick={(event) => onResolve('action_taken', event.currentTarget)}
          >
            Oznacz podjęte działanie
          </Button>
        </div>
      </div>

      <Link
        to={`/app/community/thread/${report.threadId}`}
        className="rs-admin-community-native-link"
      >
        Otwórz wątek
      </Link>
    </div>
  );
};

const AdminThreadInspector: React.FC<{
  thread: CommunityThread | null;
  pending: boolean;
  error: string | null;
  onCommand: (action: AdminThreadCommand, trigger: HTMLButtonElement) => void;
}> = ({ thread, pending, error, onCommand }) => {
  if (!thread) {
    return (
      <div className="rs-admin-community-inspector-empty">
        <p className="text-sm font-semibold text-primary-light-100">Wybierz wątek</p>
        <p className="mt-1 text-sm leading-relaxed text-primary-light-400">
          Polecenia moderacyjne pojawią się tylko dla wybranego stanu.
        </p>
      </div>
    );
  }

  const unavailable = thread.status === 'deleted';
  const hidden = thread.status === 'hidden';
  const closed = thread.status === 'closed';

  return (
    <div className="min-w-0" data-testid="admin-thread-inspector">
      <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-primary-light-400">
        {getAdminThreadCategoryLabel(thread)}
      </p>
      <h3 className="mt-1 break-words text-base font-semibold text-primary-light-100">
        {thread.title || 'Wątek bez tytułu'}
      </h3>
      <p className="mt-1 break-words text-xs text-primary-light-400">
        {thread.authorDisplayNameSnapshot || 'Autor niedostępny'} ·{' '}
        {formatCommunityDate(thread.lastActivityAt)}
      </p>

      <dl className="rs-admin-community-facts">
        <div>
          <dt>Stan</dt>
          <dd>{getAdminThreadStatusLabel(thread)}</dd>
        </div>
        <div>
          <dt>Przypięcie</dt>
          <dd>{thread.isPinned ? 'Przypięty' : 'Nieprzypięty'}</dd>
        </div>
        <div>
          <dt>Wpisy</dt>
          <dd>{thread.postsCount ?? 0}</dd>
        </div>
        <div>
          <dt>Zgłoszenia</dt>
          <dd>{thread.reportCount ?? 0}</dd>
        </div>
      </dl>

      {error && (
        <div role="alert" className="mt-3">
          <Alert variant="error">{error}</Alert>
        </div>
      )}

      {unavailable ? (
        <div className="rs-admin-community-note">
          <p className="text-sm font-semibold text-primary-light-200">
            Treść oznaczona jako usunięta
          </p>
          <p className="mt-1 text-xs leading-relaxed text-primary-light-400">
            Bieżące callables nie udostępniają legalnej akcji przywrócenia dla tego stanu.
          </p>
        </div>
      ) : (
        <div className="rs-admin-community-decision">
          <p className="text-sm font-semibold text-primary-light-100">Polecenia wątku</p>
          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
            {hidden ? (
              <Button
                type="button"
                disabled={pending}
                isLoading={pending}
                className="!min-h-11"
                onClick={(event) => onCommand('restore_thread', event.currentTarget)}
              >
                Przywróć wątek
              </Button>
            ) : (
              <>
                <Button
                  type="button"
                  variant={closed ? 'secondary' : 'neutral'}
                  disabled={pending}
                  isLoading={pending}
                  className="!min-h-11"
                  onClick={(event) =>
                    onCommand(closed ? 'reopen_thread' : 'close_thread', event.currentTarget)
                  }
                >
                  {closed ? 'Otwórz ponownie' : 'Zamknij wątek'}
                </Button>
                <Button
                  type="button"
                  variant="neutral"
                  disabled={pending}
                  className="!min-h-11"
                  onClick={(event) =>
                    onCommand(thread.isPinned ? 'unpin_thread' : 'pin_thread', event.currentTarget)
                  }
                >
                  {thread.isPinned ? 'Odepnij' : 'Przypnij'}
                </Button>
                <Button
                  type="button"
                  variant="error"
                  disabled={pending}
                  className="!min-h-11"
                  onClick={(event) => onCommand('hide_thread', event.currentTarget)}
                >
                  Ukryj wątek
                </Button>
              </>
            )}
          </div>
        </div>
      )}

      <Link to={`/app/community/thread/${thread.id}`} className="rs-admin-community-native-link">
        Otwórz wątek
      </Link>
    </div>
  );
};

const AdminReportOccurrenceStatus: React.FC<{
  report: CommunityReport;
  occurrence: CommunityReportOccurrence;
  state: 'resolved' | 'superseded';
  thread: CommunityThread | null;
}> = ({ report, occurrence, state, thread }) => {
  const targetLabel = report.targetType === 'post' ? 'Wpis' : 'Wątek';
  const title = thread?.title || 'Target niedostępny w załadowanym zestawie';
  const statusLabel =
    state === 'resolved' ? 'wystąpienie rozstrzygnięte' : 'wystąpienie zastąpione';

  return (
    <div className="min-w-0" data-testid={`admin-report-occurrence-${state}`}>
      <div className="rs-admin-community-inspector-heading">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-primary-light-400">
            {targetLabel} · {statusLabel}
          </p>
          <h3 className="mt-1 break-words text-base font-semibold text-primary-light-100">
            {title}
          </h3>
        </div>
      </div>
      <dl className="rs-admin-community-facts">
        <div>
          <dt>Wystąpienie</dt>
          <dd>#{occurrence.occurrenceVersion}</dd>
        </div>
        <div>
          <dt>Stan</dt>
          <dd>
            {state === 'resolved'
              ? occurrence.resolutionStatus || report.status
              : 'Nowsze zgłoszenie jest już bieżące'}
          </dd>
        </div>
        <div>
          <dt>Data zgłoszenia</dt>
          <dd>{formatCommunityDate(occurrence.createdAt)}</dd>
        </div>
      </dl>
      <div className="rs-admin-community-note">
        <p className="text-sm font-semibold text-primary-light-100">
          {state === 'resolved'
            ? 'To dokładne wystąpienie zostało już rozstrzygnięte.'
            : 'To dokładne wystąpienie zostało zastąpione nowszym zgłoszeniem.'}
        </p>
        <p className="mt-1 text-sm leading-relaxed text-primary-light-400">
          Nie otwieramy nowszego wystąpienia z tego powiadomienia. Odczyt dotyczy wyłącznie tego
          eventu.
        </p>
      </div>
      <Link
        to={`/app/community/thread/${report.threadId}`}
        className="rs-admin-community-native-link"
      >
        Otwórz wątek
      </Link>
    </div>
  );
};

export const AdminCommunityPage: React.FC = () => {
  const compact = useCompactAdminCommunity();
  const [searchParams] = useSearchParams();
  const targetReportId = String(searchParams.get('reportId') || '').trim();
  const targetOccurrenceId = String(searchParams.get('occurrenceId') || '').trim();
  const targetOccurrenceVersion = Number(searchParams.get('occurrenceVersion') || '');
  const hasOccurrenceIntent = Boolean(targetOccurrenceId) || searchParams.has('occurrenceVersion');
  const targetNotificationId = String(searchParams.get('notificationId') || '').trim();
  const [reports, setReports] = useState<CommunityReport[]>([]);
  const [threads, setThreads] = useState<CommunityThread[]>([]);
  const [reportsLoading, setReportsLoading] = useState(true);
  const [threadsLoading, setThreadsLoading] = useState(true);
  const [reportsError, setReportsError] = useState<string | null>(null);
  const [reportNotificationReadbackError, setReportNotificationReadbackError] = useState<
    string | null
  >(null);
  const [targetOccurrence, setTargetOccurrence] = useState<CommunityReportOccurrence | null>(null);
  const [targetOccurrenceState, setTargetOccurrenceState] = useState<
    'current-open' | 'resolved' | 'superseded' | null
  >(null);
  const [targetOccurrenceLoading, setTargetOccurrenceLoading] = useState(false);
  const [targetOccurrenceError, setTargetOccurrenceError] = useState<string | null>(null);
  const [threadsError, setThreadsError] = useState<string | null>(null);
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [mobileReportOpen, setMobileReportOpen] = useState(false);
  const [mobileThreadOpen, setMobileThreadOpen] = useState(false);
  const [announcementOpen, setAnnouncementOpen] = useState(false);
  const [announcementSubmitting, setAnnouncementSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState<AdminModerationConfirmation | null>(null);
  const [pendingTargetKeys, setPendingTargetKeys] = useState<Set<string>>(() => new Set());
  const [actionErrors, setActionErrors] = useState<Record<string, string>>({});
  const [success, setSuccess] = useState<{ message: string; threadId?: string } | null>(null);
  const pendingTargetKeysRef = useRef<Set<string>>(new Set());
  const reportSelectionReturnFocusRef = useRef<HTMLElement | null>(null);
  const appliedReportTargetRef = useRef<string | null>(null);
  const appliedOccurrenceTargetRef = useRef<string | null>(null);
  const handledReportReadbacksRef = useRef<Set<string>>(new Set());
  const threadSelectionReturnFocusRef = useRef<HTMLElement | null>(null);
  const confirmationReturnFocusRef = useRef<HTMLElement | null>(null);
  const announcementTriggerRef = useRef<HTMLButtonElement | null>(null);
  const announcementInitialFocusRef = useRef<HTMLInputElement | null>(null);
  const reportModalCloseRef = useRef<HTMLButtonElement | null>(null);
  const threadModalCloseRef = useRef<HTMLButtonElement | null>(null);
  const confirmationCancelRef = useRef<HTMLButtonElement | null>(null);
  const announcementEyebrowId = useId();
  const announcementTitleId = useId();
  const reportModalTitleId = useId();
  const threadModalTitleId = useId();
  const confirmationTitleId = useId();

  const loadReports = useCallback(async (showLoading = true): Promise<CommunityReport[]> => {
    if (showLoading) setReportsLoading(true);
    setReportsError(null);
    try {
      const nextReports = await communityService.getReportsForAdmin('all');
      setReports(nextReports);
      setSelectedReportId((current) =>
        current && nextReports.some((report) => report.id === current && report.status === 'open')
          ? current
          : null
      );
      return nextReports;
    } catch (loadError) {
      console.error('Nie udało się pobrać zgłoszeń społeczności:', loadError);
      setReportsError(getAdminCommunityError(loadError));
      throw loadError;
    } finally {
      if (showLoading) setReportsLoading(false);
    }
  }, []);

  const loadThreads = useCallback(async (showLoading = true): Promise<CommunityThread[]> => {
    if (showLoading) setThreadsLoading(true);
    setThreadsError(null);
    try {
      const nextThreads = await communityService.getThreadsForAdmin();
      setThreads(nextThreads);
      setSelectedThreadId((current) =>
        current && nextThreads.some((thread) => thread.id === current) ? current : null
      );
      return nextThreads;
    } catch (loadError) {
      console.error('Nie udało się pobrać wątków społeczności:', loadError);
      setThreadsError(getAdminCommunityError(loadError));
      throw loadError;
    } finally {
      if (showLoading) setThreadsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadReports().catch(() => undefined);
    void loadThreads().catch(() => undefined);
  }, [loadReports, loadThreads]);

  useEffect(() => {
    if (compact) return;
    setMobileReportOpen(false);
    setMobileThreadOpen(false);
  }, [compact]);

  const openReports = useMemo(
    () => reports.filter((report) => report.status === 'open'),
    [reports]
  );
  const threadById = useMemo(
    () => new Map(threads.map((thread) => [thread.id, thread])),
    [threads]
  );
  const selectedReport = useMemo(
    () => openReports.find((report) => report.id === selectedReportId) || null,
    [openReports, selectedReportId]
  );
  const targetReport = useMemo(
    () =>
      targetReportId ? openReports.find((report) => report.id === targetReportId) || null : null,
    [openReports, targetReportId]
  );
  const targetReportAny = useMemo(
    () => (targetReportId ? reports.find((report) => report.id === targetReportId) || null : null),
    [reports, targetReportId]
  );
  const selectedReportThread = selectedReport
    ? threadById.get(selectedReport.threadId) || null
    : null;
  const selectedThread = selectedThreadId ? threadById.get(selectedThreadId) || null : null;
  const hiddenThreads = threads.filter((thread) => thread.status === 'hidden').length;
  const closedThreads = threads.filter((thread) => thread.status === 'closed').length;
  const targetReportUnavailable = Boolean(
    !reportsLoading &&
      !reportsError &&
      targetReportId &&
      (hasOccurrenceIntent ? !targetReportAny : !targetReport)
  );

  useEffect(() => {
    if (!hasOccurrenceIntent) {
      appliedOccurrenceTargetRef.current = null;
      setTargetOccurrence(null);
      setTargetOccurrenceState(null);
      setTargetOccurrenceError(null);
      return;
    }
    if (
      reportsLoading ||
      reportsError ||
      !targetReportAny ||
      appliedOccurrenceTargetRef.current ===
        `${targetReportId}:${targetOccurrenceId}:${targetOccurrenceVersion}`
    ) {
      return;
    }

    const targetKey = `${targetReportId}:${targetOccurrenceId}:${targetOccurrenceVersion}`;
    appliedOccurrenceTargetRef.current = targetKey;
    setTargetOccurrenceLoading(true);
    setTargetOccurrenceError(null);
    setTargetOccurrence(null);
    setTargetOccurrenceState(null);

    if (
      !targetOccurrenceId ||
      !Number.isInteger(targetOccurrenceVersion) ||
      targetOccurrenceVersion < 1
    ) {
      setTargetOccurrenceError('Nie udało się rozpoznać pełnego celu tego wystąpienia.');
      setTargetOccurrenceLoading(false);
      return;
    }

    void communityService
      .getReportOccurrenceForAdmin(targetReportId, targetOccurrenceId)
      .then((occurrence) => {
        if (!occurrence || occurrence.reportId !== targetReportId) {
          setTargetOccurrenceError('Nie znaleziono wskazanego wystąpienia.');
          return;
        }
        if (
          occurrence.id !== targetOccurrenceId ||
          occurrence.occurrenceVersion !== targetOccurrenceVersion
        ) {
          setTargetOccurrenceError('Wskazane wystąpienie nie pasuje do powiadomienia.');
          return;
        }

        const latestOccurrenceId = String(targetReportAny.latestOccurrenceId || '').trim();
        const latestOccurrenceVersion = Number(targetReportAny.latestOccurrenceVersion);
        const isResolved =
          occurrence.resolutionStatus === 'reviewed' ||
          occurrence.resolutionStatus === 'dismissed' ||
          occurrence.resolutionStatus === 'action_taken';
        const superseded =
          Boolean(latestOccurrenceId) &&
          (latestOccurrenceId !== occurrence.id ||
            latestOccurrenceVersion > occurrence.occurrenceVersion);
        const currentOpen =
          latestOccurrenceId === occurrence.id &&
          latestOccurrenceVersion === occurrence.occurrenceVersion &&
          targetReportAny.status === 'open' &&
          !occurrence.resolutionStatus;

        if (!currentOpen && !superseded && !isResolved) {
          setTargetOccurrenceError('Nie udało się potwierdzić bieżącego stanu wystąpienia.');
          return;
        }

        setTargetOccurrence(occurrence);
        setTargetOccurrenceState(
          currentOpen ? 'current-open' : superseded ? 'superseded' : 'resolved'
        );
        if (currentOpen) {
          setSelectedReportId(targetReportAny.id);
        } else {
          setSelectedReportId(null);
        }
        if (compact) setMobileReportOpen(true);
      })
      .catch((loadError) => {
        console.error('Nie udało się pobrać wystąpienia zgłoszenia społeczności:', loadError);
        setTargetOccurrenceError('Nie udało się pobrać wskazanego wystąpienia.');
      })
      .finally(() => setTargetOccurrenceLoading(false));
  }, [
    compact,
    reportsError,
    reportsLoading,
    hasOccurrenceIntent,
    targetOccurrenceId,
    targetOccurrenceVersion,
    targetReportAny,
    targetReportId,
  ]);

  useEffect(() => {
    if (hasOccurrenceIntent) return;
    if (!targetReportId) {
      appliedReportTargetRef.current = null;
      return;
    }
    if (reportsLoading || reportsError || appliedReportTargetRef.current === targetReportId) {
      return;
    }

    appliedReportTargetRef.current = targetReportId;
    if (!targetReport) {
      return;
    }

    setSelectedReportId(targetReport.id);
    if (compact) {
      setMobileReportOpen(true);
    }
  }, [compact, hasOccurrenceIntent, reportsError, reportsLoading, targetReport, targetReportId]);

  useEffect(() => {
    if (
      reportsLoading ||
      reportsError ||
      !targetReportId ||
      !targetNotificationId ||
      (hasOccurrenceIntent
        ? !targetOccurrence || !targetOccurrenceState
        : !targetReport || selectedReportId !== targetReportId) ||
      (compact && !mobileReportOpen)
    ) {
      return;
    }

    const readbackKey = `${targetNotificationId}:${targetReportId}:${
      hasOccurrenceIntent ? `${targetOccurrenceId}:${targetOccurrenceVersion}` : 'legacy'
    }`;
    if (handledReportReadbacksRef.current.has(readbackKey)) {
      return;
    }
    handledReportReadbacksRef.current.add(readbackKey);
    setReportNotificationReadbackError(null);
    let cancelled = false;

    const readback = hasOccurrenceIntent
      ? notificationService.markCommunityReportNotificationAsRead({
          notificationId: targetNotificationId,
          reportId: targetReportId,
          occurrenceId: targetOccurrenceId,
          occurrenceVersion: targetOccurrenceVersion,
        })
      : notificationService.markCommunityReportNotificationAsRead({
          notificationId: targetNotificationId,
          reportId: targetReportId,
        });

    void readback
      .then((readBack) => {
        if (readBack || cancelled) {
          return;
        }
        handledReportReadbacksRef.current.delete(readbackKey);
        setReportNotificationReadbackError(
          'Nie udało się potwierdzić tego powiadomienia. Pozostaje ono nieprzeczytane.'
        );
      })
      .catch((readError) => {
        handledReportReadbacksRef.current.delete(readbackKey);
        console.error('Failed to read back Community report notification:', readError);
        if (!cancelled) {
          setReportNotificationReadbackError(
            'Nie udało się potwierdzić tego powiadomienia. Pozostaje ono nieprzeczytane.'
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    compact,
    mobileReportOpen,
    reportsError,
    reportsLoading,
    hasOccurrenceIntent,
    targetOccurrence,
    targetOccurrenceId,
    targetOccurrenceState,
    targetOccurrenceVersion,
    selectedReportId,
    targetNotificationId,
    targetReport,
    targetReportId,
  ]);

  const setTargetPending = (targetKey: string, pending: boolean) => {
    setPendingTargetKeys((current) => {
      const next = new Set(current);
      if (pending) next.add(targetKey);
      else next.delete(targetKey);
      return next;
    });
  };

  const runConfirmedModeration = async (
    pendingConfirmation: AdminModerationConfirmation
  ): Promise<boolean> => {
    const { targetKey, payload } = pendingConfirmation;
    if (pendingTargetKeysRef.current.has(targetKey)) return false;
    pendingTargetKeysRef.current.add(targetKey);
    setTargetPending(targetKey, true);
    setActionErrors((current) => {
      const next = { ...current };
      delete next[targetKey];
      return next;
    });
    setSuccess(null);

    try {
      await communityService.moderate(payload);

      if (payload.action === 'resolve_report') {
        const nextReports = await loadReports(false);
        const canonicalReport = nextReports.find((report) => report.id === payload.reportId);
        if (!canonicalReport || canonicalReport.status !== payload.status) {
          throw new Error('Nie udało się potwierdzić rozstrzygnięcia w kolejce zgłoszeń.');
        }
        setSelectedReportId(null);
        setMobileReportOpen(false);
      } else {
        const nextThreads = await loadThreads(false);
        const canonicalThread = nextThreads.find((thread) => thread.id === payload.threadId);
        const readbackMatches =
          canonicalThread &&
          ((payload.action === 'hide_thread' && canonicalThread.status === 'hidden') ||
            (payload.action === 'restore_thread' && canonicalThread.status === 'open') ||
            (payload.action === 'close_thread' && canonicalThread.status === 'closed') ||
            (payload.action === 'reopen_thread' && canonicalThread.status === 'open') ||
            (payload.action === 'pin_thread' && canonicalThread.isPinned === true) ||
            (payload.action === 'unpin_thread' && canonicalThread.isPinned === false));
        if (!readbackMatches) {
          throw new Error('Nie udało się potwierdzić nowego stanu w rejestrze wątków.');
        }
      }

      setSuccess({ message: pendingConfirmation.successMessage });
      return true;
    } catch (actionError) {
      setActionErrors((current) => ({
        ...current,
        [targetKey]: getAdminCommunityError(actionError),
      }));
      return false;
    } finally {
      pendingTargetKeysRef.current.delete(targetKey);
      setTargetPending(targetKey, false);
    }
  };

  const requestReportResolution = (
    status: 'dismissed' | 'action_taken',
    trigger: HTMLButtonElement
  ) => {
    if (!selectedReport) return;
    if (compact) {
      setMobileReportOpen(false);
      confirmationReturnFocusRef.current = reportSelectionReturnFocusRef.current;
    } else {
      confirmationReturnFocusRef.current = trigger;
    }
    const targetName = selectedReportThread?.title || selectedReport.threadId;
    setConfirmation({
      kind: 'report',
      targetKey: `report:${selectedReport.id}`,
      title:
        status === 'dismissed'
          ? `Oddalić zgłoszenie „${targetName}”?`
          : `Potwierdzić działanie dla „${targetName}”?`,
      message:
        status === 'dismissed'
          ? 'Zgłoszenie zniknie z otwartej kolejki jako niewymagające działania. Treść wątku nie zostanie zmieniona.'
          : 'Zgłoszenie zniknie z otwartej kolejki jako obsłużone. Ta decyzja nie wykona dodatkowej moderacji treści.',
      confirmLabel: status === 'dismissed' ? 'Oddal zgłoszenie' : 'Potwierdź działanie',
      payload:
        status === 'dismissed'
          ? {
              action: 'resolve_report',
              reportId: selectedReport.id,
              status: 'dismissed',
              resolutionNote: 'Nie wymaga działania.',
            }
          : {
              action: 'resolve_report',
              reportId: selectedReport.id,
              status: 'action_taken',
              resolutionNote: 'Podjęto działanie moderacyjne.',
              actionTaken: 'manual_review',
            },
      successMessage: 'Rozstrzygnięcie potwierdzone w kolejce zgłoszeń.',
    });
  };

  const requestThreadCommand = (action: AdminThreadCommand, trigger: HTMLButtonElement) => {
    if (!selectedThread) return;
    if (compact) {
      setMobileThreadOpen(false);
      confirmationReturnFocusRef.current = threadSelectionReturnFocusRef.current;
    } else {
      confirmationReturnFocusRef.current = trigger;
    }

    const copy: Record<
      AdminThreadCommand,
      { title: string; message: string; confirmLabel: string; successMessage: string }
    > = {
      hide_thread: {
        title: `Ukryć wątek „${selectedThread.title}”?`,
        message: 'Wątek przestanie być widoczny dla zwykłych uczestników społeczności.',
        confirmLabel: 'Ukryj wątek',
        successMessage: 'Ukrycie potwierdzone w rejestrze wątków.',
      },
      restore_thread: {
        title: `Przywrócić wątek „${selectedThread.title}”?`,
        message:
          'Wątek wróci do stanu otwartego i znów będzie widoczny dla uprawnionych uczestników.',
        confirmLabel: 'Przywróć wątek',
        successMessage: 'Przywrócenie potwierdzone w rejestrze wątków.',
      },
      close_thread: {
        title: `Zamknąć wątek „${selectedThread.title}”?`,
        message: 'Uczestnicy zachowają odczyt, ale nie będą mogli dodawać kolejnych odpowiedzi.',
        confirmLabel: 'Zamknij wątek',
        successMessage: 'Zamknięcie potwierdzone w rejestrze wątków.',
      },
      reopen_thread: {
        title: `Otworzyć ponownie „${selectedThread.title}”?`,
        message: 'Wątek wróci do stanu otwartego zgodnie z istniejącym kontraktem kategorii.',
        confirmLabel: 'Otwórz ponownie',
        successMessage: 'Ponowne otwarcie potwierdzone w rejestrze wątków.',
      },
      pin_thread: {
        title: `Przypiąć wątek „${selectedThread.title}”?`,
        message: 'Wątek otrzyma istniejący stan przypięcia. Jego treść i dostęp nie zmienią się.',
        confirmLabel: 'Przypnij wątek',
        successMessage: 'Przypięcie potwierdzone w rejestrze wątków.',
      },
      unpin_thread: {
        title: `Odpiąć wątek „${selectedThread.title}”?`,
        message: 'Wątek straci stan przypięcia. Jego treść i dostęp nie zmienią się.',
        confirmLabel: 'Odepnij wątek',
        successMessage: 'Odpięcie potwierdzone w rejestrze wątków.',
      },
    };

    setConfirmation({
      kind: 'thread',
      targetKey: `thread:${selectedThread.id}`,
      ...copy[action],
      payload: { action, threadId: selectedThread.id },
    });
  };

  const handleAnnouncementCreated = async (threadId: string) => {
    const nextThreads = await loadThreads(false);
    if (!nextThreads.some((thread) => thread.id === threadId)) {
      throw new Error('Nie udało się potwierdzić ogłoszenia w rejestrze wątków.');
    }
    setSelectedThreadId(threadId);
    setSuccess({ message: 'Ogłoszenie potwierdzone w rejestrze wątków.', threadId });
    setAnnouncementOpen(false);
  };

  const reportPending = selectedReport
    ? pendingTargetKeys.has(`report:${selectedReport.id}`)
    : false;
  const threadPending = selectedThread
    ? pendingTargetKeys.has(`thread:${selectedThread.id}`)
    : false;
  const confirmationPending = confirmation ? pendingTargetKeys.has(confirmation.targetKey) : false;
  const reportInspector = (
    <AdminReportInspector
      report={selectedReport}
      thread={selectedReportThread}
      pending={reportPending}
      error={selectedReport ? actionErrors[`report:${selectedReport.id}`] || null : null}
      onResolve={requestReportResolution}
    />
  );
  const targetOccurrenceInspector =
    targetOccurrence &&
    targetReportAny &&
    (targetOccurrenceState === 'resolved' || targetOccurrenceState === 'superseded') ? (
      <AdminReportOccurrenceStatus
        report={targetReportAny}
        occurrence={targetOccurrence}
        state={targetOccurrenceState}
        thread={threadById.get(targetReportAny.threadId) || null}
      />
    ) : null;
  const reportInspectorContent = hasOccurrenceIntent ? (
    targetOccurrenceLoading ? (
      <div className="rs-admin-community-inspector-empty" aria-busy="true">
        <p className="text-sm font-semibold text-primary-light-100">Ładowanie wystąpienia</p>
        <p className="mt-1 text-sm leading-relaxed text-primary-light-400">
          Sprawdzamy dokładny stan wskazanego zgłoszenia.
        </p>
      </div>
    ) : targetOccurrenceError ? (
      <div className="rs-admin-community-read-error" role="alert">
        <Alert variant="error">{targetOccurrenceError}</Alert>
      </div>
    ) : (
      targetOccurrenceInspector || reportInspector
    )
  ) : (
    reportInspector
  );
  const threadInspector = (
    <AdminThreadInspector
      thread={selectedThread}
      pending={threadPending}
      error={selectedThread ? actionErrors[`thread:${selectedThread.id}`] || null : null}
      onCommand={requestThreadCommand}
    />
  );

  return (
    <div className="rs-admin-community-page space-y-4">
      <section className="rs-admin-community-identity" aria-labelledby="admin-community-title">
        <div className="rs-admin-community-identity-top">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ratuj-300">
              Workbench moderacji
            </p>
            <h2
              id="admin-community-title"
              className="mt-1 break-words text-lg font-semibold text-primary-light-100 sm:text-xl"
            >
              Moderacja społeczności
            </h2>
            <p className="mt-1 max-w-3xl text-sm leading-relaxed text-primary-light-300">
              Najpierw rozstrzygaj otwarte zgłoszenia. Zmiany treści wykonuj osobno i tylko wtedy,
              gdy potwierdza je bieżący stan wątku.
            </p>
          </div>
          <div className="rs-admin-community-identity-actions">
            <Link to="/admin" className="rs-admin-community-return-link">
              Wróć do panelu
            </Link>
            <Button
              ref={announcementTriggerRef}
              type="button"
              onClick={() => {
                setSuccess(null);
                setAnnouncementOpen(true);
              }}
              icon={<MegaphoneIcon className="h-4 w-4" />}
              className="!min-h-11"
            >
              Nowe ogłoszenie
            </Button>
          </div>
        </div>

        <dl className="rs-admin-community-metrics" aria-label="Stan załadowanego zestawu">
          <div>
            <dt>Otwarte zgłoszenia</dt>
            <dd>{reportsLoading && reports.length === 0 ? '—' : openReports.length}</dd>
          </div>
          <div>
            <dt>Ukryte wątki</dt>
            <dd>{threadsLoading && threads.length === 0 ? '—' : hiddenThreads}</dd>
          </div>
          <div>
            <dt>Zamknięte wątki</dt>
            <dd>{threadsLoading && threads.length === 0 ? '—' : closedThreads}</dd>
          </div>
        </dl>
        <p className="mt-2 text-[11px] text-primary-light-400">
          Liczniki dotyczą załadowanego zestawu.
        </p>
      </section>

      {success && (
        <div className="rs-admin-community-success" role="status">
          <span>{success.message}</span>
          {success.threadId && (
            <Link to={`/app/community/thread/${success.threadId}`}>Otwórz wątek</Link>
          )}
        </div>
      )}

      {reportNotificationReadbackError && (
        <Alert variant="error">{reportNotificationReadbackError}</Alert>
      )}

      <section aria-labelledby="admin-community-report-queue-title">
        <div className="rs-admin-community-section-heading">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-primary-light-400">
              Priorytet operacyjny
            </p>
            <h2
              id="admin-community-report-queue-title"
              className="mt-1 text-base font-semibold text-primary-light-100"
            >
              Otwarte zgłoszenia
            </h2>
          </div>
          {!reportsLoading && !reportsError && (
            <p className="text-xs text-primary-light-400">
              {openReports.length === 1 ? '1 sprawa' : `${openReports.length} spraw`}
            </p>
          )}
        </div>

        {targetReportUnavailable && (
          <Alert variant="warning" className="mb-3" title="Zgłoszenie jest niedostępne">
            Nie można otworzyć wskazanego zgłoszenia w bieżącej otwartej kolejce. Powiadomienie
            pozostaje nieprzeczytane.
          </Alert>
        )}

        <div className="rs-admin-community-report-workbench">
          <div className="rs-admin-community-list-surface" aria-busy={reportsLoading}>
            {reportsError && (
              <div className="rs-admin-community-read-error" role="alert">
                <Alert variant="error">Nie udało się pobrać kolejki: {reportsError}</Alert>
                <Button
                  type="button"
                  size="sm"
                  variant="neutral"
                  className="!min-h-11"
                  onClick={() => void loadReports().catch(() => undefined)}
                >
                  Spróbuj ponownie
                </Button>
              </div>
            )}
            {reportsLoading && reports.length === 0 ? (
              Array.from({ length: 4 }, (_, index) => (
                <AdminCommunityRowSkeleton key={index} label="Ładowanie zgłoszenia" />
              ))
            ) : openReports.length === 0 && !reportsError ? (
              <div className="rs-admin-community-positive-empty">
                <p className="text-sm font-semibold text-primary-light-100">Kolejka jest czysta</p>
                <p className="mt-1 text-sm text-primary-light-400">Brak otwartych zgłoszeń.</p>
              </div>
            ) : (
              openReports.map((report) => {
                const thread = threadById.get(report.threadId) || null;
                const selected = selectedReportId === report.id;
                return (
                  <button
                    key={report.id}
                    type="button"
                    className={`rs-admin-community-report-row ${
                      selected ? 'rs-admin-community-row--selected' : ''
                    }`}
                    aria-pressed={selected}
                    onClick={(event) => {
                      reportSelectionReturnFocusRef.current = event.currentTarget;
                      setSelectedReportId(report.id);
                      if (compact) setMobileReportOpen(true);
                    }}
                  >
                    <span className="rs-admin-community-row-kicker">
                      {report.targetType === 'post' ? 'Wpis' : 'Wątek'} ·{' '}
                      {getReportReasonLabel(report.reason)}
                    </span>
                    <span className="rs-admin-community-row-title">
                      {thread?.title || 'Target niedostępny w załadowanym zestawie'}
                    </span>
                    <span className="rs-admin-community-row-context">
                      {thread
                        ? `${getAdminThreadCategoryLabel(thread)} · ${getAdminThreadStatusLabel(thread)}`
                        : `${report.threadId} · brak kontekstu wątku`}
                    </span>
                    <span className="rs-admin-community-row-meta">
                      {getCommunityRoleLabel(report.reporterRoleSnapshot)} ·{' '}
                      {formatCommunityDate(report.createdAt)}
                    </span>
                    {report.message && (
                      <span className="rs-admin-community-row-note">{report.message}</span>
                    )}
                  </button>
                );
              })
            )}
          </div>

          {!compact && (
            <aside className="rs-admin-community-inspector" aria-label="Inspektor zgłoszenia">
              {reportInspectorContent}
            </aside>
          )}
        </div>
      </section>

      <section aria-labelledby="admin-community-thread-register-title" className="pt-2">
        <div className="rs-admin-community-section-heading">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-primary-light-400">
              Obszar pomocniczy
            </p>
            <h2
              id="admin-community-thread-register-title"
              className="mt-1 text-base font-semibold text-primary-light-100"
            >
              Rejestr moderacji wątków
            </h2>
          </div>
          {!threadsLoading && !threadsError && (
            <p className="text-xs text-primary-light-400">{threads.length} w zestawie</p>
          )}
        </div>

        <div className="rs-admin-community-thread-workbench">
          <div className="rs-admin-community-list-surface" aria-busy={threadsLoading}>
            {threadsError && (
              <div className="rs-admin-community-read-error" role="alert">
                <Alert variant="error">Nie udało się pobrać wątków: {threadsError}</Alert>
                <Button
                  type="button"
                  size="sm"
                  variant="neutral"
                  className="!min-h-11"
                  onClick={() => void loadThreads().catch(() => undefined)}
                >
                  Spróbuj ponownie
                </Button>
              </div>
            )}
            {threadsLoading && threads.length === 0 ? (
              Array.from({ length: 4 }, (_, index) => (
                <AdminCommunityRowSkeleton key={index} label="Ładowanie wątku" />
              ))
            ) : threads.length === 0 && !threadsError ? (
              <div className="rs-admin-community-positive-empty">
                <p className="text-sm font-semibold text-primary-light-100">Brak wątków</p>
                <p className="mt-1 text-sm text-primary-light-400">
                  Rejestr nie zawiera obecnie żadnych pozycji.
                </p>
              </div>
            ) : (
              threads.map((thread) => {
                const selected = selectedThreadId === thread.id;
                const stateParts = [
                  getAdminThreadStatusLabel(thread),
                  thread.isPinned ? 'Przypięty' : null,
                ].filter(Boolean);
                return (
                  <button
                    key={thread.id}
                    type="button"
                    className={`rs-admin-community-thread-row ${
                      selected ? 'rs-admin-community-row--selected' : ''
                    }`}
                    aria-pressed={selected}
                    onClick={(event) => {
                      threadSelectionReturnFocusRef.current = event.currentTarget;
                      setSelectedThreadId(thread.id);
                      if (compact) setMobileThreadOpen(true);
                    }}
                  >
                    <span className="rs-admin-community-row-kicker">
                      {getAdminThreadCategoryLabel(thread)}
                    </span>
                    <span className="rs-admin-community-row-title">
                      {thread.title || 'Wątek bez tytułu'}
                    </span>
                    <span className="rs-admin-community-row-context">
                      {thread.authorDisplayNameSnapshot || 'Autor niedostępny'} ·{' '}
                      {formatCommunityDate(thread.lastActivityAt)}
                    </span>
                    <span className="rs-admin-community-row-meta">{stateParts.join(' · ')}</span>
                  </button>
                );
              })
            )}
          </div>

          {!compact && (
            <aside className="rs-admin-community-inspector" aria-label="Akcje wybranego wątku">
              {threadInspector}
            </aside>
          )}
        </div>
      </section>

      <Modal
        isOpen={
          compact &&
          mobileReportOpen &&
          (Boolean(selectedReport) ||
            Boolean(targetOccurrenceInspector) ||
            Boolean(targetOccurrenceError))
        }
        onClose={() => setMobileReportOpen(false)}
        ariaLabelledBy={reportModalTitleId}
        initialFocusRef={reportModalCloseRef}
        returnFocusRef={reportSelectionReturnFocusRef}
        placement="bottom-responsive"
        className="rounded-b-none sm:rounded-lg"
      >
        <div className="rs-admin-community-mobile-inspector">
          <div className="rs-admin-community-modal-heading">
            <h2 id={reportModalTitleId} className="text-base font-semibold text-primary-light-100">
              Inspektor zgłoszenia
            </h2>
            <button
              ref={reportModalCloseRef}
              type="button"
              className="rs-community-utility"
              onClick={() => setMobileReportOpen(false)}
              aria-label="Zamknij inspektor zgłoszenia"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          {reportInspectorContent}
        </div>
      </Modal>

      <Modal
        isOpen={compact && mobileThreadOpen && Boolean(selectedThread)}
        onClose={() => setMobileThreadOpen(false)}
        ariaLabelledBy={threadModalTitleId}
        initialFocusRef={threadModalCloseRef}
        returnFocusRef={threadSelectionReturnFocusRef}
        placement="bottom-responsive"
        className="rounded-b-none sm:rounded-lg"
      >
        <div className="rs-admin-community-mobile-inspector">
          <div className="rs-admin-community-modal-heading">
            <h2 id={threadModalTitleId} className="text-base font-semibold text-primary-light-100">
              Moderacja wątku
            </h2>
            <button
              ref={threadModalCloseRef}
              type="button"
              className="rs-community-utility"
              onClick={() => setMobileThreadOpen(false)}
              aria-label="Zamknij moderację wątku"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          {threadInspector}
        </div>
      </Modal>

      <Modal
        isOpen={announcementOpen}
        onClose={() => {
          if (!announcementSubmitting) setAnnouncementOpen(false);
        }}
        ariaLabelledBy={`${announcementEyebrowId} ${announcementTitleId}`}
        initialFocusRef={announcementInitialFocusRef}
        returnFocusRef={announcementTriggerRef}
        placement="bottom-responsive"
        className="rounded-b-none sm:rounded-lg"
      >
        <div className="p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] sm:p-4">
          <div className="rs-admin-community-modal-heading">
            <div>
              <p
                id={announcementEyebrowId}
                className="text-xs font-semibold uppercase tracking-[0.14em] text-ratuj-300"
              >
                Nowe ogłoszenie
              </p>
              <h2
                id={announcementTitleId}
                className="mt-1 text-lg font-semibold text-primary-light-100"
              >
                Ogłoszenia społeczności
              </h2>
            </div>
            <button
              type="button"
              className="rs-community-utility"
              disabled={announcementSubmitting}
              onClick={() => setAnnouncementOpen(false)}
              aria-label="Zamknij formularz ogłoszenia"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          <ThreadForm
            category={COMMUNITY_CATEGORY_BY_SLUG.get('ogloszenia')!}
            canCreate
            initialFocusRef={announcementInitialFocusRef}
            onSubmittingChange={setAnnouncementSubmitting}
            onCreated={handleAnnouncementCreated}
          />
        </div>
      </Modal>

      <Modal
        isOpen={confirmation !== null}
        onClose={() => {
          if (!confirmationPending) setConfirmation(null);
        }}
        dialogRole="alertdialog"
        ariaLabelledBy={confirmationTitleId}
        initialFocusRef={confirmationCancelRef}
        returnFocusRef={confirmationReturnFocusRef}
      >
        <div className="space-y-4 p-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-primary-light-400">
              Potwierdzenie moderacji
            </p>
            <h2
              id={confirmationTitleId}
              className="mt-1 break-words text-base font-semibold text-primary-light-100"
            >
              {confirmation?.title}
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-primary-light-300">
              {confirmation?.message}
            </p>
          </div>
          {confirmation && actionErrors[confirmation.targetKey] && (
            <div role="alert">
              <Alert variant="error">{actionErrors[confirmation.targetKey]}</Alert>
            </div>
          )}
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button
              ref={confirmationCancelRef}
              type="button"
              variant="neutral"
              disabled={confirmationPending}
              className="!min-h-11"
              onClick={() => setConfirmation(null)}
            >
              Anuluj
            </Button>
            <Button
              type="button"
              variant={confirmation?.payload.action === 'hide_thread' ? 'error' : 'primary'}
              disabled={confirmationPending}
              isLoading={confirmationPending}
              className="!min-h-11"
              onClick={async () => {
                if (!confirmation) return;
                const completed = await runConfirmedModeration(confirmation);
                if (completed) setConfirmation(null);
              }}
            >
              {confirmation?.confirmLabel || 'Potwierdź'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
